/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState, ReactElement, ChangeEvent, ComponentType } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import { Container } from 'react-bootstrap';
import type {
  ColumnMenuTab,
  DefaultMenuItem,
  FillOperationParams,
  GetMainMenuItemsParams,
  GridApi,
  MenuItemDef,
  ProcessCellForExportParams,
  GridReadyEvent,
  ICellRendererParams,
  RowGroupOpenedEvent,
  RowHeightParams,
} from 'ag-grid-community';
import BombiixAGGrid, { ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDef, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { NewTextArea } from '../../../../Components/PIM/common/NewTextArea';
import { BombiixModal } from '../../../../Components/BombiixModal';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import ConfirmationPopupBase from '../../../../Components/ModalPopup/ConfirmationPopup';
import ProductInfoSubHeaderBase from '../Subheader';
import CompletionModal from '../CompletionModal';
import ProductGroupModalBase from '../ProductGroupModal';
import AddProductGroupModalBase from '../AddProductGroupModal';
import { useProductInformationState } from '../Provider';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { getLicensorAccess, saveMultiAttribute, getMultiCustomAttribute, SaveCustomAttribute } from '../../../SystemMasters/Store/SystemMasters.action';
import { fetchColorListLibrary } from '../../../Library/ColorLibary/store/ColorLibrary.action';
import { fetchProductGroup } from '../../../RangePlan/ProductList/store/ProductList.action';
import CommonColorWayAddBase from '../../../../Components/CommonColorWay/CommonColorWayAdd';
import AddMultiDropOptBase from '../../../../Components/MultiCustomAttributeModal/addMultiDropOpt';
import AddCustomDropOptBase from '../../../../Components/AddCustomAttributeModal/addCustomDropOption';
import {
  addProduct,
  addProductGroup,
  archivedProduct,
  duplicateProduct,
  bulkUpdateProducts,
  checkDamExist,
  deleteProductAllDam,
  getProductDependentData,
  productChangeFactory,
  productChangeGrading,
  clearDescriptionSearch,
  deleteProductData,
  getCounterValueNumber,
  getDescriptionevelopmentOrder,
  getEditedProductDetails,
  patchProductsInList,
  patchProductRows,
  getOtherProductTags,
  getProductCompletion,
  getReorderList,
  trashProduct,
  unlinkDevOrder,
  bulkCreateProducts,
} from '../../store/DevelopmentOrder.action';
import { buildProductColumns, buildDynamicColumns, buildMultiCustomColumns, isMultiValueField, buildDynamicOptionMap, isDynamicDateField } from './useProductColumns';
import { buildNameMap, mapProductToRow, NameMaps, productsToShow } from './rowMapper';
import { buildVisibleRowActions, buildMoreRowActions, buildInlineRowActions, buildBulkRowActions, RowActionHandlers, buildDraftRowActions } from './rowActions';
import {
  emptyDraftRow,
  validateDraft,
  buildProductPayload,
  REQUIRED_FIELDS,
  validateInlineRows,
  buildDirtyPayload,
  changedFields,
  overCharLimitFields,
  clearOutOfScopeDynamicValues,
  clearOutOfScopeMultiValues,
  resetProductTypeScopedFields,
  isProductTypeChanged,
  buildDuplicatePayload,
  isDraftId,
  buildBulkCreatePayload,
} from './useInlineRow';
import {
  resolveFloatyMode,
  isSelectionEditable,
  selectionToEditRows,
  collectDirtyRows,
  partitionBulkResult,
  clipboardCellText,
  clipboardCellValue,
  fillCellValue,
  withRefreshedLabels,
  isFieldChanged,
  preserveLockedIds,
  collectDestructiveChanges,
  collectDraftDestructiveChanges,
  createExclusiveRunner,
  applyDestructiveCascades,
  gradingImpactOfBrandChange,
  expandSelectionToProducts,
  clusterChildIds,
  MAX_MULTI_EDIT_PRODUCTS,
  pinRowOrder,
  GRADING_SCOPE_FIELDS,
  SERVER_CHECKED_DESTRUCTIVE_FIELDS,
  CLIPBOARD_LIST_SEPARATOR,
  IDENTIFIER_CHANGE_FIELD,
  collectIdentifierChanges,
  changesIntroducedBy,
  planEditConfirmation,
  unacknowledgedChanges,
  mergeByWarning,
  warningsOf,
  revertEdit,
  identifierChangesIntroducedBy,
  optionIdsOf,
  fieldForSaveError,
  mapWithConcurrency,
  SERVER_CHECK_CONCURRENCY,
  identifierSyncFailedLabels,
  customAttributeClipboardText,
  normaliseCellValue,
  MAX_MULTI_ADD_PRODUCTS,
  partitionBulkCreateResult,
  isUnmatchedPaste,
  isChangeAcknowledged,
} from './multiEdit';
import { parentsToRewarm } from './reorderCache';
import type {
  AcknowledgedByRow,
  AcknowledgedWarning,
  BulkDetailResult,
  BulkUpdateFailure,
  BulkCreateResult,
  BulkUpdateResult,
  ConfirmationGroup,
  DestructiveChange,
  GradingLibraryEntry,
} from './multiEdit';
import { useInlineOptions } from './useInlineOptions';
import { generateAutoProductNumber, AutoIdConfig, nextFreeCounter } from './autoIdNumber';
import RowActions from './RowActionsCell';
import SelectionCell from './SelectionCell';
import SelectionHeader from './SelectionHeader';
import ExpandAllHeader from './ExpandAllHeader';
import { DynamicColumn, InlineEditConfig, MultiCustomAttribute, LockedField, LockedOption, ProductItem, RefEntity, SelectOptionMap, TableRow } from './types';
import { hasDependentData, type DependentDataFlags } from '../CreateProduct/productTypeChange';
import './ProductTableView.css';
import ConfirmButtonPopupBase from '../../../../Components/ModalPopup/ConfirmButtonPopup';
import { fetchProductIdentifierAxisUsage } from '../../ProductIdentifierTSX/services/product-identifier.service';
import { selectionsFromProduct, usageFromSavedProduct, wouldDeleteIdentifierRows } from '../CreateProduct/identifierAxisUsage';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import Chat from '../../../Chat/Chat';
import ProductTypeChangeWarning from '../CreateProduct/ProductTypeChangeWarning';
import {
  additionalVariableOptionIds,
  additionalVariableScopeKey,
  factoryCountriesFor,
  matchedAdditionalVariable,
  matchesParents,
  reconcileSupplierFactories,
  ScopedAdditionalVariable,
  toAdditionalVariablesSelection,
} from './cascadeOptions';
import type { IdentifierAxisUsage } from '../../ProductIdentifierTSX/types';
import { isCalendarDay } from '../../../../Components/BombiixAGGrid/fillClipboard';
import type { InlineOptions } from './useInlineOptions';
import { useDraftRows } from './useDraftRows';

const ConfirmationPopup = ConfirmationPopupBase as unknown as ComponentType<Record<string, unknown>>;
const ConfirmButtonPopup = ConfirmButtonPopupBase as unknown as ComponentType<Record<string, unknown>>;
const ProductGroupModal = ProductGroupModalBase as unknown as ComponentType<Record<string, unknown>>;
const AddProductGroupModal = AddProductGroupModalBase as unknown as ComponentType<Record<string, unknown>>;
const ProductInfoSubHeader = ProductInfoSubHeaderBase as unknown as ComponentType<Record<string, unknown>>;
const CommonColorWayAdd = CommonColorWayAddBase as unknown as ComponentType<Record<string, unknown>>;
const AddMultiDropOpt = AddMultiDropOptBase as unknown as ComponentType<Record<string, unknown>>;
const AddCustomDropOpt = AddCustomDropOptBase as unknown as ComponentType<Record<string, unknown>>;

/** An exit the unsaved-changes guard blocked, replayed once the user chooses to discard. */
type PendingExit = (() => void) | null;

/** The store slices this page reads, narrowed to the fields it actually uses. */
interface TableViewStoreState {
  SystemMastersDetails: {
    subBrandAccess?: unknown;
    productStatusList?: unknown[];
    pcpCustomAttribute?: unknown[];
    sizeRangesMasterList?: unknown;
    catBasedOnPT?: unknown;
  };
  Users: { logInRights?: unknown; sectionAccess?: { masters?: { productGroupsAccess?: unknown } } };
  UserDetails?: { isSupplierLoggedIn?: unknown; userInfo?: { userId?: string; role?: string } };
  Measuremets?: { descProductList?: unknown[] };
  BomLibraryDetails?: { supplierFactories?: unknown; ReferenceSupplier?: unknown };
}

/**
 * Grid identity for a row: the path-joined `__rowKey` when present, else the product id. A
 * reorder id can repeat across parents, so only the path key identifies a cluster child row.
 */
const rowKeyOf = (row?: TableRow | null): string => String(row?.__rowKey ?? row?._id ?? '');

/**
 * Table-view page for Product Information. Renders the app-standard BombiixAGGrid with the
 * same product list the card view uses, exposes the card's per-row actions, and reuses the
 * card's modals (completion, product group, archive/trash/delete confirmations).
 */
function ProductTableView(): ReactElement {
  const history = useHistory();
  const dispatch = useDispatch() as (arg: unknown) => Promise<any>;
  const { t } = useTranslation('common');
  const { t: tProductInfo } = useTranslation('product-information');
  const reducerData = useSelector(
    (redState: TableViewStoreState) => ({
      SystemMastersDetails: redState.SystemMastersDetails,
      Users: redState.Users,
      UserDetails: redState.UserDetails,
      Measuremets: redState.Measuremets,
      BomLibraryDetails: redState.BomLibraryDetails,
    }),
    shallowEqual,
  );
  const { SystemMastersDetails, Users, UserDetails, Measuremets, BomLibraryDetails } = reducerData;
  const { subBrandAccess, productStatusList = [] } = SystemMastersDetails;
  const { isSupplierLoggedIn } = UserDetails || {};
  const { descProductList = [] } = Measuremets || {};
  const { logInRights, sectionAccess } = Users;
  const userId = UserDetails?.userInfo?.userId;
  const multiDynamicAttribute = SystemMastersDetails?.pcpCustomAttribute || [];
  const { CheckActionPermission } = useComponent() as unknown as {
    CheckActionPermission: (rights: unknown, module: string, section: string, action: string) => boolean;
  };
  const productAddEditAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'addEdit');
  const productDeleteAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'delete');
  const productGroupAddEditAccess = CheckActionPermission(logInRights, 'masters', 'productGroups', 'addEdit');
  const productGroupToggle = sectionAccess?.masters?.productGroupsAccess;

  const [licensorAccess, setLicensorAccess] = useState<boolean>(false);

  const [lengthEnabled, setLengthEnabled] = useState<boolean>(true);
  const [tagSuggestions, setTagSuggestions] = useState<Array<{ label: string; value: string }>>([]);
  const tagSuggestionsFetchedRef = useRef(false);
  const [reorderFullById, setReorderFullById] = useState<Record<string, ProductItem[]>>({});
  const [reorderLoadingIds, setReorderLoadingIds] = useState<Set<string>>(new Set());
  /**
   * Parents whose cluster the user currently has expanded. Read after a list refresh to re-warm
   * exactly those entries of the reorder cache, so an open cluster keeps its full child rows.
   */
  const expandedParentIdsRef = useRef<Set<string>>(new Set());
  /**
   * Bumped whenever the reorder cache is invalidated. A fetch that started under an older
   * generation discards its result, so children read before an unlink/archive cannot land in
   * the cache after it.
   */
  const reorderCacheGenerationRef = useRef(0);
  const { draftRows, startDrafts, appendDraft, addDraftBelow, removeDraft, updateDraft, dropDrafts, clearDrafts } = useDraftRows();
  /** The first draft; a duplicate draft is always the only one. */
  const draftRow = draftRows[0] ?? null;
  const hasDrafts = draftRows.length > 0;
  /** Plain-add drafts (BM-9594): several rows, one Save. A duplicate draft keeps its own tick/cross. */
  const isMultiAdd = hasDrafts && !draftRow?.parentDevOrderId;
  /** The draft whose description the autocomplete last searched for. */
  const [descSearchRowId, setDescSearchRowId] = useState<string>('');
  const [editRows, setEditRows] = useState<Record<string, TableRow>>({});
  /* Hydrated snapshot taken when bulk edit opens, so save can send only the rows that changed. */
  const [baselineRows, setBaselineRows] = useState<Record<string, TableRow>>({});
  /* rowId -> field -> message key. `__row` holds a whole-row failure returned by the bulk save. */
  const [rowErrors, setRowErrors] = useState<Record<string, Record<string, string>>>({});
  const [confirmationQueue, setConfirmationQueue] = useState<ConfirmationGroup[]>([]);
  const confirmationSeqRef = useRef(0);
  const acknowledgedWarningsRef = useRef(new WeakMap<TableRow, string[]>());
  const editReviewsRef = useRef(new Set<Promise<boolean>>());
  const exclusiveSettle = useMemo(() => createExclusiveRunner(), []);
  /* The batch as it stands now. The destructive-change dialogs run one after another, and each
     fires async work before the next opens — reading these through refs keeps a later dialog from
     acting on the snapshot captured when the sequence began. */
  const editRowsRef = useRef<Record<string, TableRow>>({});
  const draftRowsRef = useRef<TableRow[]>([]);
  const baselineRowsRef = useRef<Record<string, TableRow>>({});
  const gradingLibraryRef = useRef<GradingLibraryEntry[]>([]);
  const [isHydrating, setIsHydrating] = useState<boolean>(false);
  /** Rows just opened for edit stay behind the loader until their dropdown options have arrived. */
  const [awaitingRowOptions, setAwaitingRowOptions] = useState<boolean>(false);
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);
  /** Single-flight gate for the single-row and draft saves; see `createExclusiveRunner`. */
  const exclusiveSave = useMemo(() => createExclusiveRunner(), []);
  /* Detail responses kept for the session, so re-opening the same selection costs no reads. */
  const hydratedCacheRef = useRef<Map<string, ProductItem>>(new Map());
  /*
   * True while a multi-row edit is open. Bulk rows commit through the floating bar, so they must
   * NOT also carry per-row save/cancel — two commit paths for the same rows is a bug factory, and
   * a per-row save would bypass the dirty filter the bulk save relies on. Single-row edit and the
   * add draft keep their own icons.
   */
  const [isBulkEditing, setIsBulkEditing] = useState<boolean>(false);
  /*
   * A queued navigation the unsaved-changes guard intercepted. Held until the user confirms, then
   * replayed — so "Discard" continues where they were going instead of stranding them on the grid.
   */
  const [pendingExit, setPendingExit] = useState<PendingExit>(null);
  /** Supplier ids the product already consumes (POs, costings, samples); locked in the suppliers cell. */
  const [lockedSupplierIds, setLockedSupplierIds] = useState<string[]>([]);
  /** Factory ids the product already consumes (POs, costings, samples, critical path); locked in the factories cell. */
  const [lockedFactoryIds, setLockedFactoryIds] = useState<string[]>([]);
  /** The locked supplier/factory removal the "Cannot Be Removed" popup is explaining; null when closed. */
  const [lockedRemovalNotice, setLockedRemovalNotice] = useState<{ field: LockedField; names: string } | null>(null);
  const [addModal, setAddModal] = useState<{ kind: 'colourway' | 'multiCustom' | 'singleCustom'; attributeId?: string } | null>(null);
  const { state, setState, callGetDevorderListApi, userRole, hasMore } = useProductInformationState() as {
    state: Record<string, any>;
    setState: (updater: (prev: any) => any) => void;
    callGetDevorderListApi: (params: Record<string, unknown>) => void;
    userRole: string;
    hasMore: boolean;
  };
  const {
    productData,
    pageLoading,
    activeList,
    searchText,
    filterData,
    filterSort,
    pageIndex,
    scrollLoader,
    archieveTechPackState,
    deleteTechPackState,
    trashTechPackState,
    selectedIds = [],
    isSelectAll = false,
  } = state;

  /**
   * Refetch the list from the first page.
   *
   * `pageIndex` MUST be reset with it: this replaces `productData` with page one, so leaving the
   * index at its scrolled position made the next `handleLoadMore` ask for `pageIndex + 20` — after
   * scrolling to page three that skipped straight from count 0 to count 60, and rows 20-59 were
   * never fetched at all.
   *
   * The reorder cache is dropped with the list. It is keyed by parent id and only ever added to,
   * so after an unlink/archive/trash/delete of a cluster child it still held that child under its
   * old parent while the fresh page also listed it standalone — the same product twice. Expanded
   * clusters are re-warmed by the effect keyed on productData once the new page arrives.
   */
  const refreshList = () => {
    reorderCacheGenerationRef.current += 1;
    setReorderFullById({});
    setReorderLoadingIds(new Set());
    setState((prev) => ({ ...prev, pageIndex: 0 }));
    /* The grid runs with `suppressScrollOnNewData`, so replacing a scrolled list with page one
       leaves the viewport parked past the end of the new data — the table renders blank AND, being
       "at the bottom", immediately fires onLoadMore for the next page. Scrolling to the top first
       keeps the refreshed rows in view and stops that phantom fetch. */
    gridApiRef.current?.ensureIndexVisible?.(0);
    return callGetDevorderListApi({
      archived: ['archived', 'bin'].includes(activeList),
      count: 0,
      searchText,
      deleted: activeList === 'bin',
      ...filterData,
      ...filterSort,
    });
  };

  /** Fetch and append the next page when the list scrolls near the bottom (like the card list). */
  const handleLoadMore = () => {
    if (scrollLoader || !hasMore) return;
    const nextIndex = (pageIndex ?? 0) + 20;
    setState((prev) => ({ ...prev, scrollLoader: true, pageIndex: nextIndex }));
    callGetDevorderListApi({
      archived: ['archived', 'bin'].includes(activeList),
      count: nextIndex,
      searchText,
      isScrolled: true,
      deleted: activeList === 'bin',
      ...filterData,
      ...filterSort,
    });
  };

  /**
   * Fetch a product's FULL reorder data in ONE call (getReorderList → all reorders' full product
   * objects) and cache it by product id, replacing anything already cached. The list API only
   * returns minimal reorder fields, so without this the expanded child rows would show only the
   * number. Callers decide whether a fetch is wanted — this always refetches.
   * @param productId the parent product whose reorders to load
   */
  const fetchReorderDetails = useCallback(
    async (productId: string) => {
      if (!productId || reorderFullById[productId] || reorderLoadingIds.has(productId)) return;
      setReorderLoadingIds((prev) => new Set(prev).add(productId));
      const res: any = await dispatch(
        getReorderList({
          archived: ['archived', 'bin'].includes(activeList),
          deleted: activeList === 'bin',
          devOrderId: productId,
          count: 0,
          withThumbnails: true,
        }),
      );
      setReorderFullById((prev) => ({ ...prev, [productId]: (res?.reOrders as ProductItem[]) || [] }));
    },
    [dispatch, activeList],
  );

  /**
   * On first expand of a product with reorders, load its reorder rows. Cached, so re-expanding
   * never refetches; in-flight ids are tracked to avoid duplicate calls when AG Grid fires the
   * group-open event more than once.
   */
  const loadReorderDetails = useCallback(
    async (productId: string) => {
      if (!productId || reorderFullById[productId] || reorderLoadingIds.has(productId)) return;
      setReorderLoadingIds((prev) => new Set(prev).add(productId));
      await fetchReorderDetails(productId);
      setReorderLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    },
    [fetchReorderDetails, reorderFullById, reorderLoadingIds],
  );

  /** AG Grid fires this when a tree group row (a parent product) is expanded/collapsed. */
  const handleRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<TableRow>) => {
      const row = event?.data;
      if (!row || row.__isReorder || (Array.isArray(row.__path) && row.__path.length > 1)) return;
      const productId = String(row._id);
      if (!event.expanded) {
        expandedParentIdsRef.current.delete(productId);
        return;
      }
      expandedParentIdsRef.current.add(productId);
      loadReorderDetails(productId);
    },
    [loadReorderDetails],
  );

  /**
   * After the list is replaced (refresh, list switch) the cache is empty but AG Grid keeps the
   * user's expanded groups open, and it only fires onRowGroupOpened on a NEW expand. Without this
   * an open cluster would fall back to the list API's minimal reorder rows (number only). Re-fetch
   * exactly the expanded parents that are on the fresh page and not yet cached.
   */
  useEffect(() => {
    parentsToRewarm((productData || []) as ProductItem[], expandedParentIdsRef.current, reorderFullById).forEach(loadReorderDetails);
  }, [productData, reorderFullById, loadReorderDetails]);

  /**
   * Grid options: keep AG Grid's auto group column suppressed and fetch reorders on expand.
   * suppressContextMenu removes the enterprise right-click menu outright.
   *
   * Scroll-blank fix (mirrors the Sample To-Do grid, BM-9219): every cell here is a React
   * cell renderer, so AG Grid's default column virtualisation + animation-frame rendering
   * re-mounts those components asynchronously while scrolling — scrolling down then back up
   * left rows painted white until the next interaction. Disabling column virtualisation and
   * the animation-frame scheduler (plus a row buffer and no row animation / scroll-reset on
   * new data) forces the React cells to paint synchronously with their row, which stops the
   * blank-out. These are passed here (not baked into the shared BombiixAGGrid) so no other
   * grid's scroll behaviour changes.
   */
  /**
   * The inline add/edit row is taller than a list row so its tags cell can wrap chips onto a
   * second line (two 20px chip rows + gap). List rows keep the grid's standard 40px. Mirrors
   * isInlineRow, which is declared further down and so cannot be referenced here.
   */
  /** AG Grid API handle, captured on grid-ready so save-validation can scroll to + focus an invalid cell. */
  const gridApiRef = useRef<GridApi<TableRow> | null>(null);

  const getRowHeight = useCallback(
    (params: RowHeightParams<TableRow>) => {
      if (!params.data) return 40;
      const isInline = isDraftId(params.data._id) || Boolean(editRowsRef.current[String(params.data._id)]);
      return isInline ? 48 : 40;
    },
    [],
  );

  /** Capture the grid API (passed through gridOptions so the shared BombiixAGGrid is untouched). */
  const handleGridReady = useCallback((event: GridReadyEvent<TableRow>) => {
    gridApiRef.current = event.api;
  }, []);

  const [allExpanded, setAllExpanded] = useState<boolean>(false);

  /**
   * Expand or collapse every expandable row at once, mirroring the PIM product table.
   *
   * Children are fetched per product on first expand, so expanding fires one request per parent
   * that has not been loaded yet. `loadReorderDetails` already caches by id and ignores a product
   * whose fetch is in flight, so a second expand-all costs nothing and a rapid double-click cannot
   * duplicate requests. Collapsing touches no network at all.
   */
  const handleToggleExpandAll = useCallback(async () => {
    const api = gridApiRef.current;
    if (!api) return;
    const next = !allExpanded;
    setAllExpanded(next);
    const parentIds: string[] = [];
    api.forEachNode((node) => {
      if (!node?.data || !(node.master || node.group)) return;
      const row = node.data as TableRow;
      if (row.__isReorder || (Array.isArray(row.__path) && row.__path.length > 1)) return;
      if (next) parentIds.push(String(row._id));
      node.setExpanded(next);
    });
    if (next) await Promise.all(parentIds.map((id) => loadReorderDetails(id)));
  }, [allExpanded, loadReorderDetails]);

  /**
   * On a failed inline save, bring the FIRST required field that is missing into view and focus
   * its editor. Required fields are walked in create-page order (REQUIRED_FIELDS), and the
   * field's colId matches its column `field`, so the grid can scroll horizontally to it and
   * focus the inline row's cell — turning the red error
   * borders into a field the user is actually taken to. The row is located by its grid key (the
   * add draft is the top row, but an edited row — a cluster child especially — can be anywhere).
   * @param errors field -> message key from validateDraft
   * @param rowKey grid key of the row being added/edited
   */
  const focusFirstInvalidField = useCallback((errors: Record<string, string>, rowKey: string) => {
    const api = gridApiRef.current;
    if (!api || api.isDestroyed?.()) return;
    const firstField = REQUIRED_FIELDS.find((field) => errors[field]);
    if (!firstField) return;
    const rowIndex = api.getRowNode(rowKey)?.rowIndex ?? 0;
    api.ensureColumnVisible(firstField);
    api.ensureIndexVisible(rowIndex);
    api.setFocusedCell(rowIndex, firstField);
  }, []);

  /**
   * On a failed BULK save, take the user to the first offending cell and name what is missing.
   *
   * The row-level red bar is not enough on its own: the required fields sit far to the right of
   * the default viewport, so a row can be flagged with every missing cell scrolled out of sight.
   * This scrolls to the first one, focuses it, and returns the distinct column headers involved so
   * the message can say which fields rather than just that some are missing.
   * @param errorsByRow validation errors keyed by row id, then field
   * @returns the column headers of every missing field across the batch
   */
  const revealFirstInvalidBulkCell = useCallback((errorsByRow: Record<string, Record<string, string>>): string[] => {
    const api = gridApiRef.current;
    if (!api || api.isDestroyed?.()) return [];
    const hits: Array<{ rowIndex: number; field: string }> = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (hits.length || node.rowIndex == null) return;
      const rowErrs = errorsByRow[String((node.data as TableRow | undefined)?._id ?? '')];
      if (!rowErrs) return;
      const field = REQUIRED_FIELDS.find((name) => rowErrs[name]) ?? Object.keys(rowErrs)[0];
      if (field) hits.push({ rowIndex: node.rowIndex, field });
    });

    const target = hits[0];
    if (target) {
      api.ensureColumnVisible(target.field);
      api.ensureIndexVisible(target.rowIndex);
      api.setFocusedCell(target.rowIndex, target.field);
    }

    const fields: string[] = uniq(Object.values(errorsByRow).flatMap((rowErrs) => Object.keys(rowErrs)));
    return fields.map((field: string) => String(api.getColumnDef(field)?.headerName ?? field));
  }, []);

  /* selectOptions is built further down (it depends on the option hooks), but the clipboard
     converters are needed up here by gridOptions. Read it through a ref so neither has to move. */
  const selectOptionsRef = useRef<SelectOptionMap>({});
  /* Row-scoped option lists (sizes for THIS row's size range, factories for its suppliers, …).
     Same reason as the ref above: the clipboard converters are built before these exist. */
  const optionsForRowRef = useRef<InlineOptions['optionsForRow'] | null>(null);

  /** Latest custom-attribute master, for the validators declared above it. */
  const customAttributesRef = useRef<DynamicColumn[]>([]);
  const multiCustomAttributesRef = useRef<MultiCustomAttribute[]>([]);
  const catBasedOnProductTypeRef = useRef(false);

  /** Clipboard COPY — see `clipboardCellText`; dropdown / radio custom attributes resolve their option name live, see `customAttributeClipboardText`. */
  const cellDisplayText = useCallback(
    (row: TableRow | undefined, colId: string, value: unknown): string =>
      colId.startsWith('dyn_') ? customAttributeClipboardText(row, colId, value, selectOptionsRef.current[colId]) : clipboardCellText(row, colId, value),
    [],
  );

  /**
   * Clipboard PASTE — see `clipboardCellValue`.
   *
   * Resolution has to use the TARGET ROW's options, not the global map. Sizes, factories and
   * product-type processes are scoped to the row (its size range, its suppliers), so the global
   * list is either empty or belongs to a different row — every pasted name then matched nothing
   * and the multi-select cell was emptied instead of filled. Text matching none of the cell's
   * options leaves the cell as it was (see `isUnmatchedPaste`).
   * @param colId the column being pasted into
   * @param text the pasted text
   * @param row the row being pasted into
   * @returns the value to store
   */
  const cellValueFromText = useCallback((colId: string, text: unknown, row?: TableRow): unknown => {
    if (isDynamicDateField(colId, customAttributesRef.current)) {
      const day = String(text ?? '').trim();
      return day === '' || isCalendarDay(day) ? day : row?.[colId] ?? '';
    }
    const options = optionsForRowRef.current?.(row, colId) ?? selectOptionsRef.current[colId];
    const isMulti = isMultiValueField(colId);
    /* No list to resolve against yet — keep what the cell holds rather than clearing it. */
    if (isMulti && !options?.length) return row?.[colId] ?? [];
    const resolved = clipboardCellValue(options, text, isMulti);
    return isUnmatchedPaste(options, text, resolved) ? row?.[colId] ?? resolved : resolved;
  }, []);

  /**
   * Column header menu, matching the PIM grid's: pin left/right/none, autosize, reset.
   *
   * The pin entry is AG Grid's own `pinSubMenu` rather than a hand-rolled submenu, so it carries
   * the pin glyph on the parent row and the native tick against the active option — the same
   * rendering the PIM grid gets from `params.defaultItems`. The items are listed explicitly
   * instead of filtering `defaultItems` because RowGroupingModule is registered and this grid
   * runs treeData, which would otherwise let group entries into the menu.
   *
   * `resetColumns` is replaced because the built-in item resets column state only, and
   * `columnChooser` is omitted because ColumnsToolPanel is not registered (see `src/lib/agGrid`).
   */
  const buildColumnMenuItems = useCallback(
    (params: GetMainMenuItemsParams<TableRow>): (DefaultMenuItem | MenuItemDef<TableRow>)[] => {
      const resetColumns: MenuItemDef<TableRow> = {
        name: t('product-information:RESET_COLUMNS') as string,
        action: () => params.api.resetColumnState(),
      };
      if (!params.column) return [resetColumns];
      return ['pinSubMenu', 'autoSizeThis', 'autoSizeAll', 'separator', resetColumns];
    },
    [t],
  );

  const gridOptions = useMemo(
    () => ({
      groupDisplayType: 'custom' as const,
      onRowGroupOpened: handleRowGroupOpened,
      onGridReady: handleGridReady,
      suppressColumnVirtualisation: false,
      suppressAnimationFrame: false,
      suppressScrollOnNewData: true,
      animateRows: false,
      rowBuffer: 10,
      getRowHeight,
      suppressDragLeaveHidesColumns: true,
      popupParent: typeof document !== 'undefined' ? document.body : null,
      /* Copy always reads the display text, so what lands on the clipboard is what the user sees —
         pasting an id would be meaningless in a spreadsheet and meaningless back in the grid. */
      processCellForClipboard: (params: ProcessCellForExportParams<TableRow>) =>
        cellDisplayText(params.node?.data ?? undefined, params.column.getColId(), params.value),
      processCellFromClipboard: (params: ProcessCellForExportParams<TableRow>) =>
        cellValueFromText(params.column.getColId(), params.value, params.node?.data ?? undefined),
      clipboardDelimiter: '\t',
      /* No right-click menu. Copy and Paste stay on the keyboard and the fill handle; the rest of
         the enterprise menu (Pin / Autosize / Reset / Expand-all) blanks a tree-data grid. */
      suppressContextMenu: true,
      /* Paste writes cells, so it is only meaningful while rows are open for edit. */
      suppressClipboardPaste: !isBulkEditing && !hasDrafts,
      /* Undo covers the grid-driven gestures (fill, paste). Typing inside a cell's own input is
         not an AG Grid edit and is not recorded — that is the trade-off of keeping the existing
         input renderers rather than swapping in AG Grid editors. */
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      /* One range at a time. Ctrl/Cmd+click would otherwise ADD a range and keep the previous one,
         often scrolled out of view, and a single-value paste writes into every range — so a value
         pasted into Month also landed in an earlier Factory selection and wiped it. */
      cellSelection: {
        suppressMultiRanges: true,
        handle: {
          mode: 'fill' as const,
          direction: 'y' as const,
          setFillValue: (params: FillOperationParams<TableRow>) =>
            fillCellValue(params.initialValues, params.currentIndex, isMultiValueField(params.column.getColId())),
        },
      },
      defaultColDef: {
        menuTabs: ['generalMenuTab'] as ColumnMenuTab[],
        mainMenuItems: buildColumnMenuItems,
      },
    }),
    [handleRowGroupOpened, handleGridReady, getRowHeight, buildColumnMenuItems, cellDisplayText, cellValueFromText, isBulkEditing, hasDrafts],
  );

  /**
   * Adding/editing inline is only valid on the open products list. When the list switches to
   * archived or bin (the table view stays mounted across the switch), discard any in-progress
   * add draft or edit so the inline row cannot leak into Archive/Bin where creating is disallowed.
   */
  useEffect(() => {
    if (activeList !== 'open') {
      clearDrafts();
      setEditRows({});
      setLockedSupplierIds([]);
      setLockedFactoryIds([]);
      setRowErrors({});
    }
    reorderCacheGenerationRef.current += 1;
    setReorderFullById({});
    expandedParentIdsRef.current.clear();
  }, [activeList]);

  useEffect(() => {
    const fetchLicensorAccess = async () => {
      const res: any = await dispatch(getLicensorAccess());
      setLicensorAccess(res?.isActiveLicensor || false);
    };
    fetchLicensorAccess();
  }, [dispatch]);

  /** Latest id→name lookups, for handlers declared above the `nameMaps` memo. */
  const nameMapsRef = useRef<NameMaps>({});

  /**
   * Flat factory id → factoryName map from the supplierFactories master. A product row stores
   * factory selections as bare ids (no populated names), so read-only list rows would otherwise
   * show ids; this map lets rowMapper render the factory NAMES. Each master entry is a supplier
   * carrying a `factories` array of { _id, factoryName } (mirrors ProductModal / DPPProduct).
   */
  const factoryNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const suppliers = (BomLibraryDetails?.supplierFactories as Array<{ factories?: Array<{ _id?: string; factoryName?: string }> }>) || [];
    suppliers.forEach((supplier) => {
      (supplier?.factories || []).forEach((factory) => {
        if (factory?._id) map[String(factory._id)] = factory.factoryName ?? '';
      });
    });
    return map;
  }, [BomLibraryDetails?.supplierFactories]);

  /**
   * Size-value id → name map from EVERY size range in the master (not just the ones an inline
   * row has selected). A product row stores its sizes as bare value ids (sizeRangeSizes), and the
   * list api now returns those ids too — but the inline `sizeRangeSizes` option list is empty on a
   * read-only list row (it fills only from the active row's selected ranges). This flat map is
   * what lets rowMapper resolve those ids to size NAMES on every list row. Each master range
   * carries a `sizes` array of { _id, name }.
   */
  const sizeValueNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const ranges = (SystemMastersDetails?.sizeRangesMasterList as Array<{ sizes?: Array<{ _id?: string; name?: string }> }>) || [];
    ranges.forEach((range) => {
      (range?.sizes || []).forEach((size) => {
        if (size?._id) map[String(size._id)] = size.name ?? '';
      });
    });
    return map;
  }, [SystemMastersDetails?.sizeRangesMasterList]);

  /**
   * Supplier id → name map from the ReferenceSupplier master. The LIST api already resolves
   * suppliers to name strings, but the edit-detail api returns bare ids (and a saved row is
   * replaced with that detail), so the map is what keeps a just-saved row showing names.
   */
  const supplierNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const suppliers = (BomLibraryDetails?.ReferenceSupplier as Array<{ _id?: string; name?: string }>) || [];
    suppliers.forEach((supplier) => {
      if (supplier?._id) map[String(supplier._id)] = supplier.name ?? '';
    });
    return map;
  }, [BomLibraryDetails?.ReferenceSupplier]);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('loggedInUserInfo') || 'null');
      if (userData?.lengthVisible !== null && userData?.lengthVisible !== undefined) setLengthEnabled(Boolean(userData.lengthVisible));
    } catch {
      setLengthEnabled(true);
    }
  }, []);

  /**
   * The default product status the Create page pre-selects on a new product: the status
   * flagged isDefault (typically "Development"). Seeded into new inline rows so the Status
   * dropdown is not blank on add (mirrors ProductModal's default-status behaviour).
   */
  const defaultStatusId = useMemo(() => {
    const match = (productStatusList as Array<{ _id?: string; isDefault?: boolean }>).find((status) => status?.isDefault === true);
    return match?._id ?? '';
  }, [productStatusList]);

  /** Begin an inline add: a blank draft row (default status pre-selected) shown at the top of the grid. */
  const handleStartAdd = () => {
    /* Guard: creating a product is only allowed on the open list, never in archived/bin. */
    if (activeList !== 'open') return;
    setEditRows({});
    setLockedSupplierIds([]);
    setLockedFactoryIds([]);
    setRowErrors({});
    startDrafts({ ...emptyDraftRow(), status: defaultStatusId });
  };

  /**
   * Description autocomplete (mirrors the Create modal's AsyncTypeahead): while typing in the
   * DRAFT row's description, debounce-fetch matching products so the cell can show a suggestion
   * dropdown. Only for the add-draft (never edit) — matches ProductModal's `!isProductEdit`.
   * The list clears when the field is emptied or the draft is closed.
   */
  const draftDescription = (draftRows.find((row) => row._id === descSearchRowId)?.description as string) || '';
  useEffect(() => {
    if (!descSearchRowId) return undefined;
    if (!draftDescription.trim()) {
      dispatch(clearDescriptionSearch());
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(getDescriptionevelopmentOrder({ searchText: draftDescription }));
    }, 600);
    return () => clearTimeout(timer);
  }, [draftDescription, descSearchRowId, dispatch]);

  useEffect(() => {
    if (!hasDrafts) dispatch(clearDescriptionSearch());
  }, [hasDrafts, dispatch]);

  /**
   * Pick a product from the description-suggestion dropdown: fetch its full detail (same source
   * edit uses, so every reference field is populated), map it to a row, and merge into a FRESH
   * draft — pre-filling all related data while keeping it a NEW product (same draft id; number/image
   * cleared so the user supplies them). Mirrors ProductModal's duplicate-from-suggestion, but
   * inline instead of opening the modal.
   */
  const handleSelectDescSuggestion = useCallback(
    async (product: ProductItem, row?: TableRow) => {
      const rowId = String(row?._id ?? '');
      if (!product?._id || !isDraftId(rowId)) return;
      dispatch(clearDescriptionSearch());
      const detailResult = (await dispatch(getEditedProductDetails([String(product._id)]))) as BulkDetailResult | undefined;
      const detail = detailResult?.rows?.[0] || product;
      const { __product, ...mapped } = mapProductToRow(detail, { t, nameMaps: nameMapsRef.current });
      updateDraft(rowId, () => ({ ...mapped, _id: rowId, __isDraft: true, number: '', image: '', fullImg: '', parentDevOrderId: undefined, isReorder: false }));
      setRowErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== rowId)));
    },
    [dispatch, t, updateDraft],
  );

  /**
   * Begin an inline edit of an existing row.
   *
   * The product LIST API returns reference fields (brand, category, supplierType, …) as
   * display NAMES, not ids — so mapping the list item straight into the edit row leaves
   * every dropdown unable to match its option (empty selects). Instead fetch the detail
   * API (getEditedProductDetails) — the same source the CreateProduct page edits from —
   * which returns populated id objects, then map THAT. resolveId already reduces a
   * populated object to its _id, so every dropdown binds correctly.
   *
   * The clicked row's tree identity (`__path` / `__rowKey` / `__isReorder`) is carried onto the
   * edit row so an expanded cluster child edits in place: `rows` substitutes the pending edit
   * into the row holding that key, and the save knows which parent's reorders to refresh.
   * @param product the list item behind the clicked row
   * @param row the clicked grid row, absent only when a caller has no row to hand
   */
  const handleStartEdit = async (product?: ProductItem, row?: TableRow) => {
    if (!product?._id) return;
    clearDrafts();
    setRowErrors({});
    const productId = String(product._id);
    const cached = hydratedCacheRef.current.get(productId);
    const detailResult = cached ? undefined : ((await dispatch(getEditedProductDetails([productId]))) as BulkDetailResult | undefined);
    const fetched = detailResult?.rows?.[0];
    if (fetched) hydratedCacheRef.current.set(productId, fetched);
    const detail = cached || fetched || product;
    const rows = {
      [String(detail._id)]: {
        ...mapProductToRow(detail, { t, nameMaps: nameMapsRef.current }),
        thumbNailImg: product.thumbNailImg || listedThumbnailOf(productId),
        __path: (row?.__path as string[]) ?? [productId],
        __rowKey: row?.__rowKey ?? productId,
        __isReorder: Boolean(row?.__isReorder),
      },
    };
    setEditRows(rows);
    setAwaitingRowOptions(true);
    /*
     * Snapshot the opened row, exactly as the bulk path does. Without a baseline `isFieldChanged`
     * short-circuits to false, so `collectDestructiveChanges` reported nothing changed and a brand
     * change saved silently — no confirmation, and none of the dependent cleanup that follows it.
     */
    setBaselineRows(JSON.parse(JSON.stringify(rows)) as Record<string, TableRow>);
    /*
     * Suppliers the product already consumes — purchase orders, costings AND samples — computed by
     * getSuppliersInUseForPO and returned on this same edit-detail response. Locking against it
     * keeps the grid in step with the full-page Edit Product screen, which uses the identical
     * source, and avoids a second request.
     */
    const inUseSuppliers = detail?.orderSupplierId;
    setLockedSupplierIds(Array.isArray(inUseSuppliers) ? [...new Set(inUseSuppliers.map((id) => String(id)).filter(Boolean))] : []);
    const inUseFactories = detail?.orderFactoryId;
    setLockedFactoryIds(Array.isArray(inUseFactories) ? [...new Set(inUseFactories.map((id) => String(id)).filter(Boolean))] : []);
  };

  /**
   * Begin an inline DUPLICATE of an existing row.
   *
   * Duplicate opens the same inline form as Add, pre-filled with the source product's data. Like
   * edit, the LIST item carries reference NAMES (not ids), so fetch the detail API and map THAT so
   * every dropdown binds. The mapped row is coerced into a fresh draft — a draft id + __isDraft — and
   * `number`, the one field that must be unique, is cleared for the user to supply. The detail stays
   * under `__product` so the save carries every field the row never renders (factory countries,
   * product identifiers, grading groups, dates). `parentDevOrderId` holds the source product: it is
   * the duplicate endpoint's target, the cluster parent when the Cluster toggle is ticked, and its
   * presence is what marks this draft as a duplicate rather than a plain add. On save the draft goes
   * through the duplicate endpoint, which copies the image, BOM, designs, documents, costing and the
   * rest of the source under the new product exactly as the Create Product page's duplicate does.
   */
  const handleStartDuplicate = async (product?: ProductItem) => {
    if (!product?._id) return;
    setEditRows({});
    setLockedSupplierIds([]);
    setLockedFactoryIds([]);
    setRowErrors({});
    const detailResult = (await dispatch(getEditedProductDetails([String(product._id)]))) as BulkDetailResult | undefined;
    const detail = detailResult?.rows?.[0] || product;
    const seed: TableRow = {
      ...mapProductToRow(detail, { t, nameMaps: nameMapsRef.current }),
      number: '',
      parentDevOrderId: product._id,
      isReorder: false,
    };
    const draftId = startDrafts(seed);
    const draft: TableRow = { ...seed, _id: draftId, __isDraft: true };
    /*
     * Snapshot the copy as opened, exactly as an edit does. Without a baseline `isFieldChanged` is
     * always false, so moving the copy to another Brand raised no confirmation and the source's
     * grading groups travelled with the new brand into the duplicate.
     */
    setBaselineRows({ [draftId]: JSON.parse(JSON.stringify(draft)) as TableRow });
  };

  /**
   * Open the "add option" modal for a cell's "+" button. The field name selects the modal:
   * the Colourway(s) cell opens the new-colourway modal; an mca_<id> cell opens the
   * multi-custom-attribute add modal for that attribute (mirrors ProductModal's + buttons).
   */
  const handleAddOption = useCallback((field: string) => {
    if (field === 'colourLibraries') {
      setAddModal({ kind: 'colourway' });
    } else if (field.startsWith('mca_')) {
      setAddModal({ kind: 'multiCustom', attributeId: field.slice(4) });
    } else if (field.startsWith('dyn_')) {
      setAddModal({ kind: 'singleCustom', attributeId: field.slice(4) });
    }
  }, []);

  /** Close the colourway add modal and refetch the colourway list so the new one is selectable. */
  const handleCloseColourwayAdd = useCallback(() => {
    setAddModal(null);
    if (userId) dispatch(fetchColorListLibrary({ loggedInUserId: userId }));
  }, [dispatch, userId]);

  /**
   * Persist new multi-custom-attribute values (mirrors ProductModal.saveMultiCustomAttribute):
   * save the new key-names, refetch the attribute master so the new values appear in the
   * mca_ dropdown, then close.
   */
  const handleSaveMultiCustom = useCallback(
    async (data: unknown) => {
      await dispatch(saveMultiAttribute({ keyNames: JSON.stringify(data) }, t));
      await dispatch(getMultiCustomAttribute({ isActive: true }));
      setAddModal(null);
    },
    [dispatch, t],
  );

  /** Cancel any inline add/edit and discard local changes. */
  const handleCancelInline = () => {
    clearDrafts();
    setDescSearchRowId('');
    setEditRows({});
    setBaselineRows({});
    setRowErrors({});
    setLockedSupplierIds([]);
    setLockedFactoryIds([]);
  };

  /**
   * Open every selected product for inline edit at once (BM-9596).
   *
   * List rows carry reference NAMES, not ids, so each row must be hydrated from the detail
   * endpoint before its dropdowns can bind — the same read single-row edit performs. Every row not
   * already cached is read in ONE bulk request rather than one request per product. The hydrated
   * state is snapshotted as the baseline so save can send only the rows the user actually changed.
   */
  const handleStartBulkEdit = async () => {
    /* Over the cap: say so and open nothing. Rows stay read-only until the user trims the
       selection, so no field can be edited that the save endpoint would then reject. */
    if (selectedProductIds.length > MAX_MULTI_EDIT_PRODUCTS) {
      toasterMsg(String(t('product-information:SELECTION_CAP_ERROR', { count: selectedProductIds.length, max: MAX_MULTI_EDIT_PRODUCTS })), 'danger');
      return;
    }
    if (!isSelectionEditable(selectedProductIds)) return;
    clearDrafts();
    setLockedSupplierIds([]);
    setLockedFactoryIds([]);
    setRowErrors({});
    setIsBulkEditing(true);
    setIsHydrating(true);

    /* Cluster children ride along with their parent — hydrate and open the expanded set. */
    const ids = selectedProductIds;

    try {
      const missingIds = ids.map(String).filter((rowId) => !hydratedCacheRef.current.has(rowId));
      const fetched = missingIds.length ? ((await dispatch(getEditedProductDetails(missingIds))) as BulkDetailResult | undefined) : undefined;
      (fetched?.rows ?? []).forEach((detail) => hydratedCacheRef.current.set(String(detail._id), detail));
      const hydrated: ProductItem[] = [];
      ids.map(String).forEach((rowId) => {
        const detail = hydratedCacheRef.current.get(rowId);
        if (detail) {
          hydrated.push(detail);
          return;
        }
        /* Deliberately NOT falling back to the list item: its reference fields hold names, so the
           row would open with "Adidas" in brandId, fire dependent lookups with it, and can save the
           wrong values. Leave the row closed and name it instead. */
        const label = productList.find((product) => String(product._id) === rowId)?.number || rowId;
        toasterMsg(`${label}: ${t('product-information:BULK_EDIT_LOAD_FAILED')}`, 'danger');
      });

      const rows = selectionToEditRows(ids, hydrated, (product) => ({
        ...mapProductToRow(product, { t, nameMaps: nameMapsRef.current }),
        thumbNailImg: listedThumbnailOf(String(product._id)),
      }));
      setEditRows(rows);
      setAwaitingRowOptions(true);
      setBaselineRows(JSON.parse(JSON.stringify(rows)) as Record<string, TableRow>);
    } finally {
      setIsHydrating(false);
    }
  };

  /**
   * Translate a key the server sent. This component's namespace is `common`, where the older
   * backend keys live, but new ones belong to this module — so try `product-information` first and
   * fall back, rather than growing `common.json`.
   * @param key the i18n key from the API
   * @returns the translated message
   */
  const translateServerKey = useCallback((key: string): string => t([`product-information:${key}`, key]) as string, [t]);

  /**
   * Over-limit custom-attribute values that should block THIS save.
   *
   * The grid holds the unfiltered attribute master, so a value stored before its limit was
   * tightened — or whose attribute has since moved out of this product's brand/product-type scope —
   * sits over the limit in a cell the row cannot even edit. Blocking on that would strand the user
   * with a save they cannot complete, so only a value this edit introduced counts. A draft has no
   * baseline, so everything on it is new.
   * @param row the row being saved
   * @param baseline the row as it was when editing opened, if it had one
   * @returns `dyn_<attrId>` -> the limit it broke, for the values this edit is responsible for
   */
  const blockingCharLimits = (row: TableRow, baseline?: TableRow): Record<string, number> => {
    const broken = overCharLimitFields(row, customAttributesRef.current);
    if (!baseline) return broken;
    return Object.fromEntries(Object.entries(broken).filter(([field]) => isFieldChanged(row, baseline, field)));
  };

  /**
   * Save every changed row in one request.
   *
   * Rows identical to their baseline are skipped entirely, so selecting fifteen and editing three
   * sends three. Rows the server rejects stay open with their edits and their own message —
   * discarding them would lose the user's work.
   */
  const handleSaveAllInline = async () => {
    /*
     * Only changed rows are validated, because only changed rows are sent.
     *
     * Opening a cluster opens its children too, and a child can be a placeholder reorder whose
     * required fields were never filled in. Validating every open row let such a row — untouched,
     * and not part of the save — block the whole batch, reporting fields the user had no reason to
     * have filled. Dirty first, then validate what will actually go.
     */
    const dirtyRows = collectDirtyRows(editRows, baselineRows);
    if (!Object.keys(dirtyRows).length) {
      handleCancelInline();
      return;
    }

    const errorsByRow = validateInlineRows(dirtyRows);
    if (Object.keys(errorsByRow).length) {
      reportMissingRequired(errorsByRow);
      return;
    }

    /*
     * Same character-limit block the Create Product page applies. The cells themselves are already
     * marked — getInlineError derives that live from the value — so this only scrolls to the first
     * one and names the limit, rather than writing a second copy of the error into rowErrors.
     */
    const overLimitByRow: Record<string, Record<string, string>> = {};
    let brokenLimit = 0;
    Object.entries(dirtyRows).forEach(([rowId, row]) => {
      const broken = blockingCharLimits(row, baselineRows[rowId]);
      Object.entries(broken).forEach(([field, limit]) => {
        overLimitByRow[rowId] = { ...(overLimitByRow[rowId] || {}), [field]: 'over-limit' };
        brokenLimit = limit;
      });
    });
    if (Object.keys(overLimitByRow).length) {
      revealFirstInvalidBulkCell(overLimitByRow);
      toasterMsg(t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit: brokenLimit }), 'danger');
      return;
    }

    /* The selection cap keeps this unreachable today — only open rows are editable, and at most
       MAX are ever open. It stands because the server caps the batch too, and a named limit reads
       better than the 400 the request would otherwise come back with. */
    if (Object.keys(dirtyRows).length > MAX_MULTI_EDIT_PRODUCTS) {
      toasterMsg(String(t('product-information:BULK_SAVE_OVER_CAP', { max: MAX_MULTI_EDIT_PRODUCTS })), 'warning');
      return;
    }

    /* Dependent fields are reset whenever their owner changed, dialog or no dialog: the
       confirmation decides whether to ASK, never whether a saved product may keep suppliers or a
       sub-brand belonging to the Product Type or Brand it no longer has. */
    const cascadedRows = applyDestructiveCascades(
      dirtyRows,
      baselineRows,
      customAttributesRef.current,
      multiCustomAttributesRef.current,
      gradingLibraryRef.current,
    );
    await settleDestructiveThenCommit(
      [...collectDestructiveChanges(dirtyRows, baselineRows), ...collectIdentifierChanges(cascadedRows, baselineRows)],
      () => commitBulkSave(cascadedRows),
      (changes) => narrowDestructiveByServerState(changes, cascadedRows),
    );
  };

  /** DAM usage answers for the check in flight, so the confirm and the cleanup ask the server once. */
  const usageCheckRef = useRef<Map<string, Promise<boolean>>>(new Map());

  /**
   * Narrow the candidate changes to the products that would really lose something.
   *
   * `collectDestructiveChanges` answers what the row can answer on its own. Product Type and Brand
   * additionally turn on server state the grid never loads, which is why single edit asks the server
   * before it opens its dialog — Brand warns when DAM exists OR a grading group would be stranded,
   * Product Type when the product holds anything a type change removes (purchase orders, costing,
   * samples, gradings, DAM, BOM, PIM rows). The same checks run here, per product, and a field whose
   * products all come back clean drops out of the queue entirely.
   *
   * Checks run when a destructive field is edited, as on the product page, and only for the products
   * that edit touched; Save re-runs them only for a change nobody has been asked about yet. At most
   * SERVER_CHECK_CONCURRENCY products are checked at once, and a check that fails counts the product
   * as affected, so the user is still asked rather than losing data unwarned.
   *
   * @param changes the candidate changes from collectDestructiveChanges
   * @param rows rowId -> the rows as edited; an edit passes its own rows because state has not re-rendered yet
   * @returns the changes that still warrant a dialog, each holding only the products it affects
   */
  const narrowDestructiveByServerState = useCallback(
    async (changes: DestructiveChange[], rows: Record<string, TableRow> = editRowsRef.current): Promise<DestructiveChange[]> => {
      usageCheckRef.current = new Map();
      const narrowed: DestructiveChange[] = [];
      for (const change of changes) {
        if (change.field === IDENTIFIER_CHANGE_FIELD) {
          const atRisk = await mapWithConcurrency(change.productIds, SERVER_CHECK_CONCURRENCY, (productId) => identifierRowsAtRisk(productId, rows[productId]));
          const affected = change.productIds.filter((_productId, index) => atRisk[index]);
          if (affected.length) narrowed.push({ ...change, productIds: affected });
          continue;
        }
        if (!SERVER_CHECKED_DESTRUCTIVE_FIELDS.includes(change.field)) {
          narrowed.push(change);
          continue;
        }
        const verdicts = await mapWithConcurrency(change.productIds, SERVER_CHECK_CONCURRENCY, async (productId) => {
          try {
            if (change.field === 'supplierType') {
              if (catBasedOnProductTypeRef.current && !rows[productId]?.supplierType) return true;
              return hasDependentData((await dispatch(getProductDependentData(productId))) as DependentDataFlags | null);
            }
            return (
              (await checkDamOnce(productId)) ||
              gradingImpactOfBrandChange(rows[productId], baselineRowsRef.current[productId], gradingLibraryRef.current).losesSome
            );
          } catch {
            return true;
          }
        });
        const affected = change.productIds.filter((_productId, index) => verdicts[index]);
        if (affected.length) narrowed.push({ ...change, productIds: affected });
      }
      return narrowed;
    },
    [dispatch],
  );

  /**
   * Run the server-side cleanup a destructive change entails, for every product it affects.
   *
   * These are the same calls the single-edit page makes for an accepted confirmation — the dialog
   * promises the dependent data is removed, so the batch has to actually remove it. They run when the
   * rows are saved, never when the dialog is accepted, so cancelling the edit removes nothing. A Product
   * Type change is not among them: it deletes nothing here, because the confirmed change travels with
   * the save (`productTypeChangeConfirmed`) and the server removes the dependent data as it writes the
   * new type, so cancelling a later dialog or a failed save leaves the products untouched. Each
   * product is handled in turn rather than in parallel: they hit shared collections (DAM, BOM,
   * grading), which is the same reason the bulk save itself is sequential.
   *
   * A failure on one product is logged and the rest continue; the save that follows reports its
   * own per-row outcome, and aborting here would leave the batch half-cleaned.
   *
   * A duplicate draft is skipped: nothing of it is saved yet, and the duplicate endpoint itself drops
   * the grading groups the copy's new scope no longer covers.
   * @param change the confirmed field and the products it affects
   */
  const runDestructiveEffects = useCallback(
    async (change: DestructiveChange) => {
      for (const productId of change.productIds) {
        if (isDraftId(productId)) continue;
        try {
          if (change.field === 'brandId') {
            if (await checkDamOnce(productId)) await dispatch(deleteProductAllDam(productId));
            /* Only when NO grading group survives the new brand — single edit keeps the compatible
               ones and wipes grading only if the compatible set is empty. */
            if (gradingImpactOfBrandChange(editRowsRef.current[productId], baselineRowsRef.current[productId], gradingLibraryRef.current).losesAll) {
              await dispatch(productChangeGrading(productId, 'deleteAll'));
            }
          } else if (change.field === 'supplierFactories') {
            const before = ((baselineRowsRef.current[productId]?.supplierFactories as string[]) || []).map(String);
            const after = new Set(((editRowsRef.current[productId]?.supplierFactories as string[]) || []).map(String));
            const removed = before.filter((factoryId) => !after.has(factoryId));
            if (removed.length) await dispatch(productChangeFactory({ productId, factoryId: removed }));
          }
        } catch (error) {
          console.error('ProductTableView: runDestructiveEffects failed', change.field, productId, error);
        }
      }
    },
    [dispatch],
  );

  /**
   * Send the batch.
   *
   * Split out of the save handler so the destructive-change confirmation can sit in between
   * without duplicating the request, the response handling or the partial-failure logic.
   * @param dirtyRows the rows to send
   */
  const commitBulkSave = async (dirtyRows: Record<string, TableRow>) => {
    setIsSavingAll(true);
    try {
      const reconciled = Object.entries(dirtyRows).map(([rowId, row]) => [rowId, reconcileAdditionalVariables(row, baselineRows[rowId])] as const);
      const payload = reconciled.map(([rowId, row]) => buildDirtyPayload(row, baselineRows[rowId], customAttributes));
      const result = (await dispatch(bulkUpdateProducts(payload, t))) as BulkUpdateResult | undefined;
      if (!result) return;

      const { openIds, errorsByRow: failureMessages } = partitionBulkResult(result);
      setEditRows((prev) => Object.fromEntries(Object.entries(prev).filter(([rowId]) => openIds.has(rowId))));
      setBaselineRows((prev) => Object.fromEntries(Object.entries(prev).filter(([rowId]) => openIds.has(rowId))));
      setRowErrors(failureMessages);

      /* Saved rows are now stale in the cache — drop them so a re-open refetches the new state. */
      (result.succeeded ?? []).forEach((id) => hydratedCacheRef.current.delete(String(id)));

      /* Patch from the rows the save itself returned; fall back to a re-read only if it omitted them. */
      if (result.rows?.length) dispatch(patchProductRows(result.rows));
      else if (result.successCount > 0) await patchProductInList((result.succeeded ?? []).map(String));

      toasterMsg(
        String(t('product-information:BULK_SAVE_SUMMARY', { saved: result.successCount, failed: result.failureCount })),
        result.failureCount > 0 ? 'warning' : 'success',
      );
      /* Name each rejected product and why: a bare count leaves the user with no way to act. */
      (result.failed ?? []).forEach((entry) => {
        const label = (dirtyRows[entry.devOrderId]?.number as string) || entry.devOrderId;
        toasterMsg(`${label}: ${describeSaveFailure(entry)}`, 'danger');
      });
      identifierSyncFailedLabels(result, dirtyRows).forEach((product) => {
        toasterMsg(String(t('product-information:IDENTIFIER_SYNC_FAILED', { product })), 'warning');
      });
    } finally {
      setIsSavingAll(false);
    }
  };

  editRowsRef.current = editRows;
  draftRowsRef.current = draftRows;
  baselineRowsRef.current = baselineRows;

  /** Whether a grid row is the one currently being added or edited inline. */
  const isInlineRow = useCallback(
    (data: TableRow): boolean => isDraftId(data?._id) || Boolean(editRows[String(data?._id)]),
    [editRows],
  );

  /** Latest rangeId → active size ids, for the size auto-select cascade (handler declared above it). */
  const sizeIdsByRangeRef = useRef<Record<string, string[]>>({});

  /** Masters the cascade reads through a ref, so the memoised edit handler never sees first-render lists. */
  const cascadeMastersRef = useRef<{
    catBasedOnPT: boolean;
    category2List: RefEntity[];
    additionalVariables: ScopedAdditionalVariable[] | null;
    supplierFactories: unknown;
  }>({
    catBasedOnPT: false,
    category2List: [],
    additionalVariables: null,
    supplierFactories: [],
  });

  /** Clear the selections that depend on a changed Product Type or category, as CreateProduct's handleSelect does. */
  const applyCascadeResets = (row: TableRow, field: string, previous?: TableRow): TableRow => {
    const next: TableRow = { ...row };
    const { catBasedOnPT, category2List } = cascadeMastersRef.current;
    if (field === 'catOneId') {
      const catTwo = category2List.find((option) => String(option._id) === String(next.catTwoId));
      if (catBasedOnPT && next.catTwoId && !(catTwo && matchesParents(catTwo, [{ relations: ['catOneIds', 'categoryOneId'], id: next.catOneId }]))) {
        Object.assign(next, { catTwoId: '' });
      }
      Object.assign(next, { catThreeId: '', catFourId: '', productTypeProcesses: [] });
    }
    if (field === 'catTwoId') Object.assign(next, { catThreeId: '', catFourId: '', productTypeProcesses: [] });
    if (field === 'catThreeId') Object.assign(next, { catFourId: '', productTypeProcesses: [] });
    if (field === 'brandId') Object.assign(next, { subBrandId: '' });
    if (field === 'licensorId') Object.assign(next, { licenseId: '', likenessId: '' });
    if (field === 'licenseId') Object.assign(next, { likenessId: '' });
    const scoped = clearOutOfScopeDynamicValues(next, field, customAttributesRef.current);
    if (field !== 'supplierType' || !isProductTypeChanged(previous, row)) return scoped;
    return resetProductTypeScopedFields(
      clearOutOfScopeMultiValues({ ...scoped, suppliers: [] }, multiCustomAttributesRef.current),
      catBasedOnProductTypeRef.current,
    );
  };

  /**
   * When the Size Range multi-select changes, auto-select the added range's active sizes into
   * Size(s) and drop a removed range's sizes — mirroring CreateProduct's `sizeRangeId` case. The
   * user can still deselect individual sizes afterwards (that edits sizeRangeSizes directly).
   * @param prevRow the row BEFORE this change (source of the previously selected ranges)
   * @param nextRow the row AFTER sizeRangeId was set (source of the newly selected ranges)
   */
  const applySizeRangeAutoSelect = (prevRow: TableRow, nextRow: TableRow): TableRow => {
    const sizeIdsByRange = sizeIdsByRangeRef.current;
    const prevRanges = Array.isArray(prevRow.sizeRangeId) ? (prevRow.sizeRangeId as string[]) : [];
    const nextRanges = Array.isArray(nextRow.sizeRangeId) ? (nextRow.sizeRangeId as string[]) : [];
    const added = nextRanges.filter((id) => !prevRanges.includes(id));
    const removed = prevRanges.filter((id) => !nextRanges.includes(id));
    let sizes = Array.isArray(nextRow.sizeRangeSizes) ? [...(nextRow.sizeRangeSizes as string[])] : [];
    removed.forEach((rangeId) => {
      const toRemove = new Set(sizeIdsByRange[rangeId] || []);
      sizes = sizes.filter((sizeId) => !toRemove.has(sizeId));
    });
    added.forEach((rangeId) => {
      sizes = [...new Set([...sizes, ...(sizeIdsByRange[rangeId] || [])])];
    });
    return { ...nextRow, sizeRangeSizes: sizes };
  };

  /**
   * Single inline-edit callback for BombiixAGGrid. Dispatches on the field: the licensed
   * toggle clears the licensor trio when turned off; category changes cascade-reset their
   * children; select values arrive as option objects and are reduced to their id (arrays
   * for multi-select). Only the active draft/edit row is mutable.
   */
  /**
   * Apply one field edit to a row, with the same cascade rules whichever way the edit arrived —
   * typed into a cell, dragged with the fill handle, or pasted. Keeping one implementation is the
   * point: a fill that skipped the category cascade would leave child categories pointing at a
   * parent the row no longer has.
   * @param prev the row before the change
   * @param field the field being written
   * @param value the new value
   * @param normalise reduces option objects to the ids the payload builder expects
   * @returns the updated row
   */
  /**
   * Apply a fill-handle drag or a clipboard paste.
   *
   * AG Grid raises one cell-changed event per written cell; the grid wrapper batches them into a
   * single call, so the whole gesture lands as ONE state update instead of one render per cell.
   *
   * Nothing is sent to the server here. Filled and pasted cells join the same dirty buffer as
   * typed ones and go out only when Save is pressed — the grid deliberately does not adopt PIM's
   * per-cell auto-save.
   *
   * Rows not open for edit are skipped. AG Grid already refuses them (the column's `editable`
   * resolves through `getCellReadOnly`, which is false only for open rows), but a fill dragged
   * past the last open row must never quietly rewrite a read-only one, so the guard is repeated
   * here where the write actually happens.
   * @param batch the cells AG Grid wrote in this gesture
   */
  const applyFieldChange = useCallback(
    (prev: TableRow | null, field: string, value: unknown, normalise: typeof normaliseCellValue): TableRow | null => {
      if (!prev) return prev;
      const normalised = field === 'isLicensedProduct' ? Boolean(value) : normalise(value);
      /*
       * Values the product already consumes downstream cannot be dropped. The menu disables those
       * options, but a fill drag, a paste, or Clear All replaces the whole array without going
       * near the menu — so the lock is re-applied here, where every edit path converges.
       */
      const nextValue =
        field === 'additionalVariables'
          ? toAdditionalVariablesSelection(
              Array.isArray(normalised) ? normalised.map(String) : [],
              matchedAdditionalVariable(prev, cascadeMastersRef.current.additionalVariables ?? []),
            )
          : preserveLockedIds(prev, field, normalised);
      if (JSON.stringify(prev[field] ?? null) === JSON.stringify(nextValue ?? null)) return prev;
      let next: TableRow = { ...prev, [field]: nextValue };
      if (field === 'isLicensedProduct' && !nextValue) Object.assign(next, { licensorId: '', licenseId: '', likenessId: '' });
      next = applyCascadeResets(next, field, prev);
      /* Size Range change → auto-select/deselect that range's sizes (mirrors Create Product). */
      if (field === 'sizeRangeId') next = applySizeRangeAutoSelect(prev, next);
      next = applySupplierFactoryCascade(prev, next);
      /* Labels last, so they reflect the cascades too — copy reads them, not the stored ids. */
      const labelled = withRefreshedLabels(prev, next, nameMapsRef.current);
      if (field !== 'additionalVariables') return labelled;
      const optionNameById = nameMapsRef.current.additionalVariables ?? {};
      return {
        ...labelled,
        additionalVariables__label: additionalVariableOptionIds(nextValue)
          .map((optionId) => optionNameById[optionId])
          .filter(Boolean)
          .join(CLIPBOARD_LIST_SEPARATOR),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleFillCells = useCallback(
    (batch: BombiixFillBatchItem<TableRow>[]) => {
      batch
        .filter(({ rowId }) => isDraftId(String(rowId)))
        .forEach(({ rowId, field, newValue }) => updateDraft(String(rowId), (row) => applyFieldChange(row, field, newValue, normaliseCellValue)));

      setEditRows((prev) => {
        let changed = false;
        const next = { ...prev };
        batch.forEach(({ rowId, field, newValue }) => {
          const key = String(rowId);
          const row = next[key];
          if (!row) return;
          const updated = applyFieldChange(row, field, newValue, normaliseCellValue);
          if (updated) {
            next[key] = updated;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      reviewFilledCells(batch, normaliseCellValue);
    },
    [applyFieldChange, updateDraft],
  );

  const handleRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const key = String(rowId);

      const updater = (prev: TableRow | null): TableRow | null => applyFieldChange(prev, field, value, normaliseCellValue);

      if (isDraftId(key)) {
        updateDraft(key, updater);
        if (field === 'description') setDescSearchRowId(key);
      } else {
        setEditRows((prev) => ({ ...prev, [key]: updater(prev[key] ?? null) as TableRow }));
      }
      reviewCellEdit(key, field, value, normaliseCellValue);

      setRowErrors((prev) => {
        if (!prev[key]?.[field]) return prev;
        return { ...prev, [key]: Object.fromEntries(Object.entries(prev[key]).filter(([name]) => name !== field)) };
      });
    },
    [applyFieldChange, updateDraft],
  );

  /**
   * Read a validation error for a field on an inline row. The add draft keeps its own error map;
   * bulk-edit rows each carry their own, so a failure on one row cannot mark the others.
   */
  const getInlineError = useCallback(
    (data: TableRow, field: string | undefined): string | undefined => {
      if (!isInlineRow(data) || !field) return undefined;
      const perRow = rowErrors[String(data?._id)];
      if (perRow?.[field]) return translateServerKey(perRow[field]);
      /* Only custom-attribute cells carry a character limit, so only they pay for the lookup. */
      if (field.startsWith('dyn_')) {
        const limit = overCharLimitFields(data, customAttributesRef.current)[field];
        if (limit) return t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit }) as string;
      }
      return undefined;
    },
    [isInlineRow, rowErrors, t, translateServerKey],
  );

  /**
   * Refresh the saved rows after an inline EDIT.
   *
   * The edit-detail API returns reference fields (brand, subBrand, supplierType, …) as POPULATED
   * objects, whereas the list API returns them as display NAME strings. The card view (which shares
   * this provider's `productData`) renders several of those fields as raw React children, so storing
   * a detail-shaped product into the shared list makes the card throw "Objects are not valid as a
   * React child" and blank the page when switching table → card view.
   *
   * So the saved ids are re-read through the LIST endpoint, which is list-shaped by construction,
   * and patched into the rows already on screen. Refetching the whole list would also have been
   * correct, but it resets paging to the first page and re-applies every filter and sort
   * server-side just to update rows the user is already looking at.
   *
   * Falls back to a full refresh whenever the targeted read cannot express the outcome — most
   * importantly when an edit pushes a product out of the active filter and its row has to go.
   * @param savedIds ids of the products that just saved
   */
  const patchProductInList = async (savedIds?: string[]) => {
    const ids = (savedIds ?? []).map(String).filter(Boolean);
    if (!ids.length) return;
    await dispatch(
      patchProductsInList(ids, {
        archived: ['archived', 'bin'].includes(activeList),
        deleted: activeList === 'bin',
      }),
    );
  };

  /**
   * Send one edited row through the single-product update and patch it back into the list.
   *
   * Split out of the save handler for the same reason as commitBulkSave: the destructive-change
   * confirmation sits in between without the request being written twice.
   * @param rows the single row to send, keyed by its id
   */
  const commitSingleSave = async (rows: Record<string, TableRow>) => {
    const source = Object.values(rows)[0];
    if (!source) return;
    /*
     * A cluster child does not live in the product list the update action patches — it lives in
     * this page's per-parent reorder cache, so that parent's reorders are refetched to show the
     * saved values. A top-level row is patched into the list by the action itself.
     */
    const parentId = source.__isReorder ? String((source.__path as string[])?.[0] ?? '') : '';
    await runExclusiveSave(async () => {
      const baseline = baselineRows[String(source._id)];
      const reconciled = reconcileAdditionalVariables(source, baseline);
      const result = (await dispatch(bulkUpdateProducts([buildDirtyPayload(reconciled, baseline, customAttributes)], t))) as BulkUpdateResult | undefined;
      if (result?.successCount) {
        handleCancelInline();
        hydratedCacheRef.current.delete(String(source._id));
        if (result.rows?.length) dispatch(patchProductRows(result.rows));
        if (parentId) await fetchReorderDetails(parentId);
        else if (!result.rows?.length) await patchProductInList([String(source._id)]);
        identifierSyncFailedLabels(result, rows).forEach((product) => {
          toasterMsg(String(t('product-information:IDENTIFIER_SYNC_FAILED', { product })), 'warning');
        });
      } else if (result?.failed?.[0]) {
        showSingleSaveFailure(String(source._id), result.failed[0]);
      }
    });
  };

  /**
   * Persist the inline row via the existing APIs: a plain draft is created, a duplicate draft (one
   * opened from a source product) is sent to the duplicate endpoint with its Cluster choice, and an
   * edited row is updated.
   */
  const handleSaveInline = async (row: TableRow) => {
    const rowId = String(row?._id ?? '');
    const source = isDraftId(rowId) ? draftRows.find((draft) => draft._id === rowId) : editRows[rowId];
    if (!source) return;
    const errors = validateDraft(source);
    if (Object.keys(errors).length) {
      setRowErrors((prev) => ({ ...prev, [rowId]: errors }));
      focusFirstInvalidField(errors, rowKeyOf(source));
      toasterMsg(t('product-information:REQUIRED_FIELDS_MESSAGE'), 'warning');
      return;
    }
    const overLimit = blockingCharLimits(source, baselineRows[String(source._id)]);
    if (Object.keys(overLimit).length) {
      toasterMsg(t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit: Object.values(overLimit)[0] }), 'danger');
      return;
    }
    if (isDraftId(rowId)) {
      const draftById = { [rowId]: source };
      const cascadedDraftRows = applyDestructiveCascades(
        draftById,
        baselineRows,
        customAttributesRef.current,
        multiCustomAttributesRef.current,
        gradingLibraryRef.current,
      );
      const draftDestructive = collectDraftDestructiveChanges(draftById, baselineRows, gradingLibraryRef.current);
      await settleDestructiveThenCommit(
        draftDestructive,
        () => commitDraftSave(cascadedDraftRows),
        async (changes) => changes,
      );
      return;
    }

    /*
     * The same confirmation the batch raises, for the one row. A Brand or Product Type change here
     * destroys the same dependent data — DAM entries, BOM, grading — so it has to be confirmed and
     * cleaned up whether the user came in through the pencil or through Edit Selected.
     */
    const rows = { [String(source._id)]: source };
    const cascadedRows = applyDestructiveCascades(rows, baselineRows, customAttributesRef.current, multiCustomAttributesRef.current, gradingLibraryRef.current);
    await settleDestructiveThenCommit(
      [...collectDestructiveChanges(rows, baselineRows), ...collectIdentifierChanges(cascadedRows, baselineRows)],
      () => commitSingleSave(cascadedRows),
      (changes) => narrowDestructiveByServerState(changes, cascadedRows),
    );
  };

  /** Whether the archive/unarchive action is permitted for the current list + role. */
  const archiveAllowed =
    (userRole !== 'Admin' && (['open', 'reorder'].includes(activeList) ? archieveTechPackState : deleteTechPackState)) || userRole === 'Admin';
  const trashAllowed = (trashTechPackState && userRole !== 'Admin') || userRole === 'Admin';

  const setProductState = setState;

  const handlers: RowActionHandlers = useMemo(
    () => ({
      onEdit: (product?: ProductItem, row?: TableRow) => handleStartEdit(product, row),
      onDuplicate: (product?: ProductItem) => handleStartDuplicate(product),
      onArchive: (product?: ProductItem) => setProductState((prev) => ({ ...prev, selectedIds: [product?._id], isArchivedModal: true })),
      onTrash: (product?: ProductItem) => setProductState((prev) => ({ ...prev, selectedIds: [product?._id], isTrashModal: true })),
      onDelete: (product?: ProductItem) => setProductState((prev) => ({ ...prev, isDeleteOpen: true, deletedDevOrderId: product?._id })),
      onGroup: (product?: ProductItem) => setProductState((prev) => ({ ...prev, isProductGroupModalOpen: true, currentProduct: product })),
      onUnlink: (product?: ProductItem) => setProductState((prev) => ({ ...prev, unlinkProductId: product?._id, isUnlinkOpen: true })),
      onHistory: (product?: ProductItem) => history.push(`/development-order/${product?._id}/history`),
      onCompletion: async (product?: ProductItem) => {
        const res: any = await dispatch(getProductCompletion(product?._id, { percentage: true }));
        if (res?.code === 200) {
          setProductState((prev) => ({ ...prev, isCompletionModalOpen: true, currentProduct: product, completionData: res.data }));
        }
      },
    }),
    [history, dispatch, setProductState],
  );

  const access = { addEdit: productAddEditAccess, delete: productDeleteAccess };
  const lastLoadedProductsRef = useRef<ProductItem[]>([]);
  lastLoadedProductsRef.current = productsToShow((productData || []) as ProductItem[], lastLoadedProductsRef.current, Boolean(pageLoading));
  const loadedProductList = lastLoadedProductsRef.current;
  /*
   * A list refresh re-applies the server sort, which would move a row the user is still editing.
   * The order is captured when editing opens and held until it ends, so an open row keeps its place.
   */
  const pinnedOrderRef = useRef<string[] | null>(null);
  const productList = useMemo(() => pinRowOrder(loadedProductList, pinnedOrderRef.current), [loadedProductList]);

  useEffect(() => {
    const isEditing = hasDrafts || Object.keys(editRows).length > 0;
    if (!isEditing) {
      pinnedOrderRef.current = null;
      return;
    }
    if (!pinnedOrderRef.current) {
      pinnedOrderRef.current = loadedProductList.map((product) => String(product._id));
    }
  }, [hasDrafts, editRows, loadedProductList]);

  /*
   * Column option cascades (brand -> sub-brand, licensor -> license, category chain) are driven by
   * ONE row. With a single row open this is exactly today's behaviour; with several open it follows
   * the first, which is why bulk edit keeps the cascade-bearing fields per-row rather than shared.
   */
  const editRowList = useMemo(() => Object.values(editRows), [editRows]);
  const activeInlineRow = draftRow || editRowList[0] || null;
  const openInlineRows = useMemo(() => [...draftRows, ...editRowList], [draftRows, editRowList]);

  /*
   * Floaty state. Open edit rows win over selection, so the button stays Save until the user saves
   * or cancels even if they untick meanwhile. Above the cap the Edit action is simply unavailable —
   * the selection itself is left alone, because select-all also serves Archive / Status / Submit.
   */
  /*
   * The Edit action is offered for ANY non-empty selection, including one over the cap. Hiding it
   * there left the user with no way to find out why — the button simply vanished. It now stays
   * put and explains the limit when pressed.
   */
  /*
   * A ticked cluster parent brings its reorder children with it — they have no checkbox of their
   * own. The count, the cap and the batch all work from this expanded list, so the number shown
   * and the limit enforced refer to the same thing the save endpoint receives.
   */
  const selectedProductIds = useMemo(
    () => expandSelectionToProducts(selectedIds as string[], productList, reorderFullById),
    [selectedIds, productList, reorderFullById],
  );
  const floatyMode = resolveFloatyMode({
    selectedCount: selectedProductIds.length,
    editingCount: editRowList.length,
    isBulkEditing,
    addDraftCount: isMultiAdd ? draftRows.length : 0,
  });
  const overSelectionCap = selectedProductIds.length > MAX_MULTI_EDIT_PRODUCTS;
  /* Save shows how many rows will actually be sent, not how many are open. */
  const dirtyRowCount = useMemo(
    () => Object.entries(editRows).filter(([rowId, row]) => changedFields(row, baselineRows[rowId]).length > 0).length,
    [editRows, baselineRows],
  );

  /** Edits exist that a save has not yet accepted — leaving now would discard them. */
  const hasUnsavedEdits = dirtyRowCount > 0 || hasDrafts;

  /*
   * Unsaved-changes guard for in-app navigation (the card-view toggle, the header links), so the
   * app's own dialog can offer Save or Discard. Tab close and reload are deliberately not guarded:
   * the browser's prompt carries no custom text and cannot save, so it only adds a second dialog.
   */
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined;

    const unblock = history.block((location: unknown) => {
      setPendingExit(() => () => {
        unblock();
        history.push(location as string);
      });
      return false;
    });

    return unblock;
  }, [hasUnsavedEdits, history]);

  /** Marks an open row that failed validation, so the save blocker is visible on the row itself. */
  const getBulkRowClass = useCallback(
    (row?: TableRow): string => {
      if (!row) return '';
      const rowId = String(row._id ?? '');
      if (!editRows[rowId] && !isDraftId(rowId)) return '';
      return rowErrors[rowId] ? 'ptv-row--error' : '';
    },
    [editRows, rowErrors],
  );

  /*
   * AG Grid evaluates getRowClass when it draws a row, not on every React render, so the edit-state
   * classes stay in the DOM after the rows close — leaving the error rule on rows that are no
   * longer open. Redraw once, on the transition to zero open rows. Redrawing on every change
   * instead would rebuild the row the user is typing in and take the caret with it.
   */
  const hadOpenEditRowsRef = useRef<boolean>(false);
  useEffect(() => {
    const openCount = Object.keys(editRows).length;
    if (hadOpenEditRowsRef.current && openCount === 0) {
      const api = gridApiRef.current;
      if (api && !api.isDestroyed?.()) api.redrawRows();
    }
    hadOpenEditRowsRef.current = openCount > 0;
  }, [editRows]);

  /** Discard the open edits and replay whatever navigation the guard intercepted. */
  const handleDiscardAndExit = useCallback(() => {
    const exit = pendingExit;
    setPendingExit(null);
    handleCancelInline();
    if (exit) exit();
  }, [pendingExit, handleCancelInline]);
  const {
    selectOptions: baseOptions,
    multiCustomAttributes,
    customAttributes,
    context: inlineContext,
    sizeIdsByRange,
    refetchCustomAttributes,
    gradingLibrary,
    optionsForRow,
    allAdditionalVariables,
    allAdditionalVariablesMap,
    rowOptionsReady,
    catBasedOnProductType,
  } = useInlineOptions(activeInlineRow, openInlineRows);
  gradingLibraryRef.current = gradingLibrary;
  cascadeMastersRef.current = {
    catBasedOnPT: Boolean(SystemMastersDetails?.catBasedOnPT),
    category2List: inlineContext.category2List,
    additionalVariables: allAdditionalVariables,
    supplierFactories: BomLibraryDetails?.supplierFactories ?? [],
  };

  /*
   * handleRowChange (declared above) reads the size-range→sizes map through this ref so it always
   * sees the latest master without being re-created on every options change.
   */
  sizeIdsByRangeRef.current = sizeIdsByRange;
  const selectOptions = useMemo(
    () => ({
      ...baseOptions,
      productNumberType: [
        { _id: 'Manual', name: t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_MANUAL') },
        { _id: 'Auto', name: t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_AUTO_GENERATED') },
      ],
      ...buildDynamicOptionMap(customAttributes),
    }),
    [baseOptions, customAttributes],
  );

  /**
   * Persist new dropdown/radio options for a SINGLE custom attribute (mirrors
   * ProductModal.saveCustomAttribute): save the new key-names, then re-fetch BOTH the
   * brand+PT-scoped master (enable set) AND the full custom-attribute master that backs the
   * column option lists, so the new option appears in the dyn_ dropdown WITHOUT a page refresh.
   */
  const handleSaveSingleCustom = useCallback(
    async (data: unknown) => {
      await dispatch(SaveCustomAttribute({ keyNames: JSON.stringify(data), title: 'custom-attribute-master' }, t));
      await refetchCustomAttributes();
      setAddModal(null);
    },
    [dispatch, t, refetchCustomAttributes],
  );

  /**
   * id→name lookups for every dropdown-backed column, derived from the same option lists the
   * inline editors use so a cell's label can never disagree with its dropdown. Needed because
   * the list API returns most reference fields as resolved NAMES while the edit-detail API
   * returns bare ids — and a saved row is replaced with that detail response.
   * Custom-attribute (`dyn_`) options are left out: an edit must not add a `__label` key the loaded
   * row never had, or the whole-row unsaved-changes comparison sees a cleared value as a change.
   */
  const nameMaps = useMemo(() => {
    const maps: Record<string, Record<string, string>> = {};
    Object.entries(selectOptions).forEach(([field, options]) => {
      if (!field.startsWith('dyn_')) maps[field] = buildNameMap(options);
    });
    maps.suppliers = supplierNameById;
    maps.supplierFactories = factoryNameById;
    maps.sizeRangeSizes = sizeValueNameById;
    maps.additionalVariables = allAdditionalVariablesMap;
    return maps;
  }, [selectOptions, supplierNameById, factoryNameById, sizeValueNameById, allAdditionalVariablesMap]);

  selectOptionsRef.current = selectOptions;
  optionsForRowRef.current = optionsForRow;

  /*
   * The edit/duplicate handlers are declared above `nameMaps` (they sit beside the other row
   * actions), so they read it through a ref that always holds the latest maps.
   */
  nameMapsRef.current = nameMaps;
  customAttributesRef.current = customAttributes;
  multiCustomAttributesRef.current = multiCustomAttributes;
  catBasedOnProductTypeRef.current = catBasedOnProductType;

  /** A row's Product Number Type is Auto when its value is 'Auto' (draft) or boolean true (edit detail). */
  const isAutoRow = useCallback((row: TableRow): boolean => row?.productNumberType === 'Auto' || row?.productNumberType === true, []);

  /** Resolve the universal auto-id config (active when refCode Product_Number_Universal is on). */
  const universalAutoId = useMemo<AutoIdConfig>(() => {
    const match = (inlineContext.autoIdMaster || []).find((item) => item.refCode === 'Product_Number_Universal' && item.isModuleActive);
    return match
      ? {
          isActive: !!match.isModuleActive,
          counterNo: match.counterNo || '',
          selectedChildProperties: (match.selectedChildProperties || []) as AutoIdConfig['selectedChildProperties'],
        }
      : { isActive: false, counterNo: '', selectedChildProperties: [] };
  }, [inlineContext.autoIdMaster]);

  /** Resolve the brand-scoped auto-id config for the active row's brand. */
  const brandAutoIdFor = useCallback(
    (brandId: unknown): AutoIdConfig => {
      const match = (inlineContext.autoIdMaster || []).find(
        (item) =>
          item.refCode === 'Product_Number' && item.isModuleActive && item.brandId?.toString() === (brandId as { toString(): string } | undefined)?.toString(),
      );
      return match
        ? {
            isActive: !!match.isModuleActive,
            counterNo: match.counterNo || '',
            selectedChildProperties: (match.selectedChildProperties || []) as AutoIdConfig['selectedChildProperties'],
          }
        : { isActive: false, counterNo: '', selectedChildProperties: [] };
    },
    [inlineContext.autoIdMaster],
  );

  /** The auto-id (icon) is available when a universal config is active or ANY brand config is active. */
  const autoIdActive = useMemo(
    () =>
      (inlineContext.autoIdMaster || []).some(
        (item) => (item.refCode === 'Product_Number_Universal' || item.refCode === 'Product_Number') && item.isModuleActive,
      ),
    [inlineContext.autoIdMaster],
  );

  /** Generate the auto product number for the given inline row (mirrors CreateProduct's icon click). */
  const handleGenerateNumber = useCallback(
    async (row: TableRow): Promise<string | undefined> => {
      const rowId = String(row?._id ?? '');
      const isDraft = isDraftId(rowId);
      const takenCounters = isDraft
        ? draftRows
            .filter((draft) => draft._id !== rowId && (universalAutoId.isActive || String(draft.brandId) === String(row?.brandId)))
            .map((draft) => draft.counterNo)
        : [];
      const result = await generateAutoProductNumber({
        universal: universalAutoId,
        brand: brandAutoIdFor(row?.brandId),
        row: row as Record<string, unknown>,
        brandList: inlineContext.brandList,
        subBrandList: inlineContext.subBrandList,
        seasonList: inlineContext.seasonList,
        monthOptions: inlineContext.monthOptions,
        yearOptions: inlineContext.yearOptions,
        category1List: inlineContext.category1List,
        category2List: inlineContext.category2List,
        category3List: inlineContext.category3List,
        category4List: inlineContext.category4List,
        productTypeList: inlineContext.productTypeList,
        colorWaysLibList: inlineContext.colorWaysLibList,
        multiDynamicAttribute: inlineContext.multiCustomAttributes,
        isExistingProduct: !isDraft,
        getCounterValue: async (brandId) => nextFreeCounter(await dispatch(getCounterValueNumber(brandId)), takenCounters),
      });
      if (!result) {
        toasterMsg(`${t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER')} generation failed. Please fill in required fields.`, 'warning');
        return undefined;
      }
      const patch = (prev: TableRow | null): TableRow | null => (prev ? { ...prev, number: result.number, counterNo: result.counterNo } : prev);
      if (isDraft) updateDraft(rowId, patch);
      else setEditRows((prev) => ({ ...prev, [rowId]: patch(prev[rowId] ?? null) as TableRow }));
      return result.number;
    },
    [universalAutoId, brandAutoIdFor, inlineContext, dispatch, t, draftRows, updateDraft],
  );

  const autoIdCellConfig = useMemo(
    () => ({ isAuto: isAutoRow, autoIdActive, onGenerate: handleGenerateNumber }),
    [isAutoRow, autoIdActive, handleGenerateNumber],
  );

  /**
   * Lazily load the tag suggestions the first time a tags cell is focused (mirrors the
   * ProductModal's ReactTags onFocus → getOtherProductTags). The list of existing tag names
   * becomes the Creatable dropdown's options; a user can still type a brand-new tag.
   */
  const handleLoadTagSuggestions = useCallback(async () => {
    if (tagSuggestionsFetchedRef.current) return;
    tagSuggestionsFetchedRef.current = true;
    const res: any = await dispatch(getOtherProductTags());
    const names: string[] = Array.isArray(res) ? res.map((entry: any) => (typeof entry === 'string' ? entry : entry?.name)).filter(Boolean) : [];
    setTagSuggestions(uniq(names).map((name: string) => ({ label: name, value: name })));
  }, [dispatch]);

  const tagsCellConfig = useMemo(
    () => ({ suggestions: tagSuggestions, onLoadSuggestions: handleLoadTagSuggestions }),
    [tagSuggestions, handleLoadTagSuggestions],
  );

  /** Description autocomplete wiring for the DescriptionCell (suggestion list + pick handler). */
  const descAutoFillConfig = useMemo(
    () => ({ suggestions: (descProductList || []) as ProductItem[], onSelect: handleSelectDescSuggestion, activeRowId: descSearchRowId }),
    [descProductList, handleSelectDescSuggestion, descSearchRowId],
  );

  /**
   * Dialog copy per destructive field, reusing the SINGLE-EDIT keys verbatim so the batch asks the
   * same question in the same words the user already knows from the product page. Brand and Sub
   * Brand share one set, exactly as they share one dialog there. Product Type has no fixed second
   * line: it names the modules the affected products actually hold data in.
   */
  const lengthLabel = useLengthLabel(t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.LENGTH'));
  const destructiveCopy = useMemo(
    () =>
      ({
        supplierType: {
          title: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE_CHANGE',
          message: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE_CHANGE_MESSAGE',
        },
        brandId: {
          title: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_CHANGE',
          message: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_MESSAGE',
          second: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_SECOND_MESSAGE',
        },
        supplierFactories: {
          title: 'EDIT_FIELD_MODAL.REMOVE_FACTORY',
          message: 'EDIT_FIELD_MODAL.FIRST_MESSAGE',
          second: 'EDIT_FIELD_MODAL.SECOND_MESSAGE',
        },
        ...Object.fromEntries(
          GRADING_SCOPE_FIELDS.map((field) => [
            field,
            {
              title: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_TITLE',
              message: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_MESSAGE',
              second: 'COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_SECOND_MESSAGE',
            },
          ]),
        ),
        [IDENTIFIER_CHANGE_FIELD]: {
          title: 'product-information:PRODUCT_IDENTIFIER_CHANGE.TITLE',
          message: 'product-information:PRODUCT_IDENTIFIER_CHANGE.MESSAGE',
          second: 'product-information:PRODUCT_IDENTIFIER_CHANGE.SECOND_MESSAGE',
          params: { variableOneName: lengthLabel },
        },
      } as Record<string, { title: string; message: string; second?: string; params?: Record<string, string> }>),
    [lengthLabel],
  );

  const edit = useMemo(() => ({ isInlineRow, getError: getInlineError }), [isInlineRow, getInlineError]);
  const editRef = useRef<InlineEditConfig>(edit);
  editRef.current = edit;
  const stableEdit = useMemo<InlineEditConfig>(
    () => ({
      isInlineRow: (row) => editRef.current.isInlineRow(row),
      getError: (row, field) => editRef.current.getError(row, field),
    }),
    [],
  );

  /** The selection id for a product row: cluster id if present, else the product id (matches the card list). */
  const productSelectionId = (product: ProductItem): string => String((product as { clusterId?: string }).clusterId ?? product._id);

  /** Toggle a single row's selection, keeping isSelectAll in sync with the visible list. */
  const handleToggleSelect = useCallback(
    (id: string, checked: boolean) => {
      setState((prev: any) => {
        const current: string[] = prev?.selectedIds || [];
        /*
         * Ticking a cluster parent takes its reorder children with it, and unticking releases
         * them — the cluster behaves as one unit from the parent's checkbox. Each child still has
         * its own checkbox, so it can be dropped from the batch individually afterwards.
         */
        const parent = (prev?.productData || []).find((product: ProductItem) => productSelectionId(product) === id);
        const cluster = parent ? [id, ...clusterChildIds(parent, reorderFullById)] : [id];
        const nextIds = checked ? uniq([...current, ...cluster]) : current.filter((selected) => !cluster.includes(selected));
        /* Select-all tracks the parent rows, which are what its own checkbox ticks. */
        const allIds = (prev?.productData || []).map((product: ProductItem) => productSelectionId(product));
        return { ...prev, selectedIds: nextIds, isSelectAll: allIds.length > 0 && allIds.every((rowId: string) => nextIds.includes(rowId)) };
      });
    },
    [setState, reorderFullById],
  );

  /** Select or clear every product in the current list (mirrors the card view's onSelectAll). */
  const handleSelectAll = useCallback(() => {
    setState((prev: any) => {
      if (!isSelectAll && Array.isArray(prev?.productData) && prev.productData.length) {
        const ids = prev.productData.map((product: ProductItem) => productSelectionId(product));
        return { ...prev, isSelectAll: true, selectedIds: uniq([...(prev?.selectedIds || []), ...ids]) };
      }
      return { ...prev, isSelectAll: false, selectedIds: [] };
    });
  }, [setState, isSelectAll]);

  /** Pinned-left selection column: select-all checkbox in the header, a per-row checkbox in each cell. */
  const selectionColumn = useMemo<BombiixColumnDef<TableRow>>(
    () => ({
      colId: '__ptv_select',
      headerName: '',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      pinned: 'left',
      sortable: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerComponent: SelectionHeader,
      headerComponentParams: {
        isSelectAll,
        /* Mirrors the PIM table: a dash when the selection is a subset of the visible rows. */
        isPartialSelection: (selectedIds as string[]).length > 0,
        onSelectAll: handleSelectAll,
        disabled: isBulkEditing,
      },
      cellRenderer: SelectionCell,
      /* The open edit rows were built from this selection, so it is frozen until the user
         saves or cancels — otherwise unticking a row leaves it open with nowhere to commit. */
      cellProps: { selectedIds, onToggle: handleToggleSelect, disabled: isBulkEditing },
    }),
    [isSelectAll, selectedIds, handleSelectAll, handleToggleSelect, isBulkEditing],
  );

  /**
   * Pinned-left expand column that sits right AFTER the checkbox. We drive expansion from
   * our own column (via ExpandChevronCell → node.setExpanded) and suppress AG Grid's
   * auto-inserted group column (groupDisplayType:'custom'), so the order is ☐ then ▸.
   */
  const expandColumn = useMemo<BombiixColumnDef<TableRow>>(
    () => ({
      colId: '__ptv_expand',
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRenderer: ExpandChevronCell,
      headerComponent: ExpandAllHeader,
      headerComponentParams: { allExpanded, onToggleExpandAll: handleToggleExpandAll, disabled: isBulkEditing },
    }),
    [allExpanded, handleToggleExpandAll, isBulkEditing],
  );

  /**
   * Opens the "Supplier / Factory Cannot Be Removed" popup naming the locked options the user just
   * tried to remove from an inline Supplier or Factory cell.
   * @param {LockedField} field - The column the removal was attempted in.
   * @param {LockedOption[]} lockedOptions - The locked options the removal hit.
   * @returns {void}
   */
  const handleLockedRemoval = useCallback((field: LockedField, lockedOptions: LockedOption[]) => {
    setLockedRemovalNotice({ field, names: lockedOptions.map((option) => option.label ?? option.name ?? '').join(', ') });
  }, []);

  /**
   * Column defs. Custom columns use the SHOW + DISABLE model (all columns always visible; gated
   * cells greyed): SINGLE custom cells disable until the row has both brand and product type (or
   * the attribute is inactive / config-locked); MULTI custom cells disable unless active AND the
   * row's product type is in the attribute's productType list. Source config comes ONLY from the
   * master (customAttributes), never unioned with the product dynamicColumn template — the template
   * lacks isActive/type/isEditable, so unioning it evaluated the disable check against undefined
   * config and mis-enabled fields; master-only keeps the disable decision deterministic.
   */
  const multiPickPreviewRef = useRef<(_rowId: string, _field: string, _value: unknown[]) => Promise<boolean>>(() => Promise.resolve(false));
  const previewMultiPick = useCallback(
    (rowId: string | number, field: string, value: unknown[]) => multiPickPreviewRef.current(String(rowId), field, value),
    [],
  );

  const columnDefs = useMemo<BombiixColumnDef<TableRow>[]>(() => {
    return [
      selectionColumn,
      expandColumn,
      ...buildProductColumns({
        subBrandAccess: !!subBrandAccess,
        licensorAccess,
        lengthEnabled,
        t,
        edit: stableEdit,
        selectOptions,
        optionsForRow,
        autoId: autoIdCellConfig,
        tags: tagsCellConfig,
        descAutoFill: descAutoFillConfig,
        onAddOption: handleAddOption,
        lockedSupplierIds,
        onMultiPick: previewMultiPick,
        lockedFactoryIds,
        onLockedRemoval: handleLockedRemoval,
      }),
      ...buildDynamicColumns(customAttributes, { t, edit: stableEdit, onAddOption: handleAddOption }),
      ...buildMultiCustomColumns(multiCustomAttributes, { edit: stableEdit, selectOptions, t, onAddOption: handleAddOption }),
    ];
  }, [
    selectionColumn,
    expandColumn,
    subBrandAccess,
    licensorAccess,
    lengthEnabled,
    selectOptions,
    multiCustomAttributes,
    customAttributes,
    stableEdit,
    optionsForRow,
    autoIdCellConfig,
    tagsCellConfig,
    descAutoFillConfig,
    lockedSupplierIds,
    previewMultiPick,
    lockedFactoryIds,
    handleLockedRemoval,
  ]);

  /* Built rows, keyed by grid row key, with the source they were built from — see `buildRow`. */
  const mappedRowCacheRef = useRef<Map<string, { source: TableRow | ProductItem; nameMaps: NameMaps; errors: unknown; row: TableRow }>>(new Map());

  /**
   * Rows for the tree grid. Each product is a top-level node (path=[id]); its reorders
   * become child nodes (path=[id, reorderId]) so they render inline under the same columns
   * (perfect header alignment, no nested grid). The add drafts sit first, on the open list only. Every row
   * carries a unique `__rowKey` (path-joined) since a reorder id can repeat across products.
   *
   * A row open for edit is substituted from `editRows`, at either level, so a cluster child shows
   * the pending edit instead of reverting to its stored values.
   */
  const rows: TableRow[] = useMemo(() => {
    const out: TableRow[] = [];
    const cache = mappedRowCacheRef.current;
    const seen = new Set<string>();

    /*
     * Reuse the previously built row whenever its source object is unchanged.
     *
     * This memo re-runs on every keystroke (editRows is a dependency), and rebuilding each row
     * object would hand AG Grid a whole new set — its immutable diff compares by reference, so
     * every row would redraw for a single character typed in one of them. Both upstream sources
     * preserve identity for untouched entries: the list reducer's targeted patch keeps the same
     * product objects for rows it did not patch, and the edit-row updater replaces only the row
     * being typed in. So reference equality is enough to tell "unchanged" from "changed", and a
     * save that patches 10 of 20 redraws 10.
     */
    const buildRow = (source: TableRow | ProductItem, key: string, build: () => TableRow, errors?: unknown): TableRow => {
      seen.add(key);
      const cached = cache.get(key);
      if (cached && cached.source === source && cached.nameMaps === nameMaps && cached.errors === errors) return cached.row;
      const row = build();
      cache.set(key, { source, nameMaps, errors, row });
      return row;
    };

    if (activeList === 'open') draftRows.forEach((draft) => out.push({ ...draft, __path: [String(draft._id)], __rowKey: String(draft._id) }));
    productList.forEach((product) => {
      const id = String(product._id);
      const source = editRows[id] ?? product;
      out.push(
        buildRow(
          source,
          id,
          () => {
            const base = editRows[id] ?? mapProductToRow(product, { t, nameMaps });
            return { ...base, __path: [id], __rowKey: id };
          },
          rowErrors[id],
        ),
      );
      const fullReorders = reorderFullById[id];
      /*
       * The reorder-list query returns the parent itself (matching `_id === parent`) alongside its
       * children, so drop the parent here — it is already rendered as this group's top-level row and
       * would otherwise appear a second time inside its own expanded view.
       */
      const reorderSource = ((fullReorders && fullReorders.length ? fullReorders : product.reOrders || []) as ProductItem[]).filter(
        (reorder) => String(reorder._id) !== id,
      );
      reorderSource.forEach((reorder) => {
        const childId = String(reorder._id);
        const childKey = `${id}/${childId}`;
        /*
         * A cluster child open for edit renders from its edit row, exactly as a top-level row
         * does. Mapping the raw reorder instead showed the list-shaped object while the cell was
         * editable: fields the list omits (brand among them) came up empty and failed the
         * required-field check, and anything typed was discarded on the next render.
         */
        const childSource = editRows[childId] ?? reorder;
        out.push(
          buildRow(
            childSource,
            childKey,
            () => {
              const base = editRows[childId] ?? mapProductToRow(reorder, { t, nameMaps });
              return { ...base, __path: [id, childId], __rowKey: childKey, __isReorder: true };
            },
            rowErrors[childId],
          ),
        );
      });
    });

    /* Drop entries for rows no longer rendered, so the cache cannot grow across list refreshes. */
    [...cache.keys()].filter((key) => !seen.has(key)).forEach((key) => cache.delete(key));
    return out;
  }, [productList, editRows, draftRows, reorderFullById, nameMaps, activeList, rowErrors]);

  const getRowId = useCallback((row: TableRow) => String(row.__rowKey ?? row._id), []);
  const getDataPath = useCallback((row: TableRow) => (row.__path as string[]) ?? [String(row._id)], []);

  const inlineActions = useMemo(() => buildInlineRowActions({ t, onSave: handleSaveInline, onCancel: handleCancelInline }), [t, draftRows, editRows]);

  /**
   * The Cluster toggle, offered only on a duplicate draft — a plain add has no source product to
   * cluster onto. Ticking it flips the draft's `isReorder`, which is all `buildProductPayload`
   * needs to emit `isReorder` + `parentDevOrderId` and save the copy into the source's cluster.
   */
  const clusterControl = useMemo(() => {
    if (!draftRow?.parentDevOrderId) return undefined;
    return {
      label: String(tProductInfo('PRODUCT_MODAL.CLUSTER_LABEL')),
      checked: Boolean(draftRow.isReorder),
      onChange: (checked: boolean) => updateDraft(String(draftRow._id), (previous) => ({ ...previous, isReorder: checked })),
    };
  }, [tProductInfo, draftRow?._id, draftRow?.parentDevOrderId, draftRow?.isReorder, updateDraft]);

  const normalVisibleActions = useMemo(
    () => buildVisibleRowActions({ t, handlers, access, activeList, isSupplier: Boolean(isSupplierLoggedIn) }),
    [t, handlers, activeList, productAddEditAccess, isSupplierLoggedIn],
  );

  const normalMoreActions = useMemo(
    () =>
      buildMoreRowActions({
        t,
        handlers,
        access,
        activeList,
        groupAllowed: Boolean(productGroupAddEditAccess && productGroupToggle),
        archiveAllowed,
        trashAllowed,
        isSupplier: Boolean(isSupplierLoggedIn),
      }),
    [
      t,
      handlers,
      activeList,
      productAddEditAccess,
      productDeleteAccess,
      productGroupAddEditAccess,
      productGroupToggle,
      archiveAllowed,
      trashAllowed,
      isSupplierLoggedIn,
    ],
  );

  /**
   * Drop one product from an open bulk edit: untick it and close its row.
   *
   * Its pending edits go with it — the row is being removed from the batch, so keeping a draft for
   * a product that is no longer in it would resurface on the next open. Baseline and error entries
   * are cleared alongside, or a stale error would keep a closed row marked as failed.
   * @param row the grid row whose product is being removed
   */
  const handleRemoveFromBulkEdit = useCallback(
    (row: TableRow) => {
      const rowId = String(row?._id ?? '');
      if (!rowId) return;
      handleToggleSelect(rowId, false);
      const drop = (prev: Record<string, TableRow>) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== rowId));
      setEditRows(drop);
      setBaselineRows(drop);
      setRowErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== rowId)));
    },
    [handleToggleSelect],
  );

  /** The lone action on a bulk-edit row: remove this product from the batch. */
  const bulkRowActions = useMemo(() => buildBulkRowActions({ t, onRemove: handleRemoveFromBulkEdit }), [t, handleRemoveFromBulkEdit]);

  /**
   * Render the per-row actions column: Save/Cancel on the inline row, the normal set for
   * product AND reorder rows (a reorder carries its own __product, so the same handlers apply).
   * While a bulk edit or a multi-add is open every other row is frozen, so a mid-batch edit,
   * archive or duplicate cannot discard what was typed into the open rows.
   */
  const renderRowActionExtras = useCallback(
    (params: ICellRendererParams<TableRow>) => {
      const row = params?.data;
      if (!row) return null;
      if (isInlineRow(row)) {
        /* Bulk rows are committed from the floating bar, so they carry no Save/Cancel — only the
           control to drop this product from the batch. */
        if (isBulkEditing && !isDraftId(row._id)) {
          return <RowActions row={row} visibleActions={bulkRowActions} moreActions={[]} />;
        }
        if (isMultiAdd) {
          const draftActions = buildDraftRowActions({
            t,
            canAdd: draftRows.length < MAX_MULTI_ADD_PRODUCTS,
            onSave: (target) => handleSaveDrafts(draftRows.filter((draft) => draft._id === target._id)),
            onAddBelow: (target) => handleAddDraftBelow(target),
            onRemove: (target) => handleRemoveDraft(target),
          });
          return <RowActions row={row} visibleActions={draftActions} moreActions={[]} disabled={isSavingAll} />;
        }
        return <RowActions row={row} visibleActions={inlineActions} moreActions={[]} clusterControl={clusterControl} />;
      }
      return <RowActions row={row} visibleActions={normalVisibleActions} moreActions={normalMoreActions(row)} disabled={isBulkEditing || isMultiAdd} />;
    },
    [isInlineRow, isBulkEditing, inlineActions, bulkRowActions, normalVisibleActions, normalMoreActions, clusterControl, isMultiAdd, draftRows, isSavingAll, customAttributes],
  );

  /* Bulk mode ends when the last row closes — whether by Cancel, a clean save, or the final
     failed row being resolved — so the per-row icons come back for ordinary single-row edits. */
  useEffect(() => {
    if (Object.keys(editRows).length === 0) setIsBulkEditing(false);
  }, [editRows]);

  const closeModals = () =>
    setState((prev) => ({ ...prev, isArchivedModal: false, isTrashModal: false, isDeleteOpen: false, isUnlinkOpen: false, loader: false }));

  const handleArchivedConfirmed = async () => {
    setState((prev) => ({ ...prev, loader: true }));
    const actedOn = uniq(state?.selectedIds);
    const res = await dispatch(archivedProduct({ devOrderIds: actedOn, archieveComment: state?.archivedComment, isArchived: activeList === 'open' }, t));
    if (res === 200) {
      setState((prev) => ({ ...prev, selectedIds: [], archivedComment: '', loader: false }));
      forgetOpenRows(actedOn);
      closeModals();
      refreshList();
    }
  };

  const handleTrashConfirmed = async () => {
    setState((prev) => ({ ...prev, loader: true }));
    const actedOn = uniq(state?.selectedIds);
    const res = await dispatch(trashProduct({ doIds: actedOn }, t));
    if (res === 200) {
      setState((prev) => ({ ...prev, selectedIds: [], loader: false }));
      forgetOpenRows(actedOn);
      closeModals();
      refreshList();
    } else {
      setState((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleDeleteConfirmed = async () => {
    const res = await dispatch(deleteProductData(state?.deletedDevOrderId, t));
    if (res === 200) {
      forgetOpenRows([state?.deletedDevOrderId]);
      closeModals();
      refreshList();
    }
  };

  const handleUnlinkConfirmed = async () => {
    const res = await dispatch(unlinkDevOrder(state?.unlinkProductId, t));
    if (res === 200) {
      closeModals();
      refreshList();
    }
  };

  const handleCloseCompletion = () => setState((prev) => ({ ...prev, isCompletionModalOpen: false, currentProduct: null, completionData: null }));
  const handleCloseGroup = () => setState((prev) => ({ ...prev, isProductGroupModalOpen: false, currentProduct: null, selectedIds: [], selectedGroups: [] }));

  /** Open the "new product group" modal from the Product Groups modal's "+" button (mirrors the card page). */
  const handleOpenProductGroup = () => setState((prev) => ({ ...prev, isAddProductGroup: true }));

  /** Close the "new product group" modal and reset its input/error/selection (mirrors the card page). */
  const handleCloseAddProductGroup = () => setState((prev) => ({ ...prev, isAddProductGroup: false, newProductGroup: '', groupError: false, selectedIds: [] }));

  /** Bind the group-name input in the "new product group" modal. */
  const handleProductGroupInput = (val: string) => setState((prev) => ({ ...prev, newProductGroup: val }));

  /**
   * Create a new product group from the modal and add the currently selected products to it
   * (mirrors the card page's handleAddProductGroup). Empty name flags a validation error;
   * on success the group list is refetched and the modal closes.
   */
  const handleAddProductGroup = async () => {
    if (!state?.newProductGroup?.trim()?.length) {
      setState((prev) => ({ ...prev, groupError: true }));
      return;
    }
    const res: any = await dispatch(
      addProductGroup(
        { productGroupName: state?.newProductGroup, seaction: 'masters', module: 'productGroups', action: 'addEdit', prodId: uniq(state?.selectedIds) },
        t,
      ),
    );
    if (res?.code === 200) {
      await dispatch(fetchProductGroup());
      handleCloseAddProductGroup();
    }
  };

  /* The dialog currently being asked: the head of the destructive-change queue. */
  const activeConfirmation: ConfirmationGroup | undefined = confirmationQueue[0];
  const activeDestructive = activeConfirmation?.changes[0];
  const activeDestructiveCopy = activeDestructive ? destructiveCopy[activeDestructive.field] : undefined;

  /**
   * Forget open rows the list no longer holds.
   *
   * Archive, Trash and Delete refresh the list but left `editRows` alone, so a row being edited
   * when it was archived stayed "open" with nothing on screen — and because a single open row hides
   * the Add / Edit slot, the bar lost that button until the page was reloaded. Only rows that
   * actually leave the list are forgotten; the rest keep their unsaved edits.
   * @param ids the products the action removed from the list
   */
  const forgetOpenRows = (ids: unknown[]) => {
    const gone = new Set(ids.filter(Boolean).map(String));
    if (!gone.size) return;
    const without = <T,>(byRowId: Record<string, T>): Record<string, T> => Object.fromEntries(Object.entries(byRowId).filter(([rowId]) => !gone.has(rowId)));
    setEditRows(without);
    setBaselineRows(without);
    setRowErrors(without);
  };

  /**
   * Send a draft through the existing endpoints: a copy opened from a source product goes to the
   * duplicate endpoint with its Cluster choice, a plain draft is created. Called directly when the
   * draft has nothing to confirm, and from the confirmation queue once every dialog is accepted.
   * @param draftsById the draft keyed by its id, after the destructive cascades were applied
   */
  const commitDraftSave = async (draftsById: Record<string, TableRow>) => {
    const draft = Object.values(draftsById)[0];
    if (!draft) return;
    await runExclusiveSave(async () => {
      const res = draft.parentDevOrderId
        ? await dispatch(duplicateProduct(String(draft.parentDevOrderId), buildDuplicatePayload(draft, customAttributes), Boolean(draft.isReorder), t))
        : await dispatch(addProduct(buildProductPayload(draft, customAttributes), t));
      if (res === 200) {
        handleCancelInline();
        refreshList();
      }
    });
  };

  /**
   * Run one inline save at a time, under the same saving overlay the batch save shows. A second
   * Save click while a request is still in flight is dropped instead of being sent again.
   * @param save the request and its follow-up
   */
  const runExclusiveSave = async (save: () => Promise<void>) => {
    await exclusiveSave(async () => {
      setIsSavingAll(true);
      try {
        await save();
      } finally {
        setIsSavingAll(false);
      }
    });
  };

  useEffect(() => {
    if (awaitingRowOptions && rowOptionsReady) setAwaitingRowOptions(false);
  }, [awaitingRowOptions, rowOptionsReady]);

  /** Inline rows are taller; heights are recomputed only when a row enters or leaves edit, never per keystroke. */
  const inlineRowsKey = `${draftRows.map((draft) => String(draft._id)).join(',')}_${Object.keys(editRows).sort().join(',')}`;
  useEffect(() => {
    const api = gridApiRef.current;
    if (api && !api.isDestroyed?.()) api.resetRowHeights();
  }, [inlineRowsKey]);
  /**
   * The warnings accepted for each open row since it was opened. The record hangs off the row's
   * baseline, which every open replaces, so reopening a row asks afresh.
   * @returns rowId -> accepted warnings
   */
  const acknowledgedByRow = (): AcknowledgedByRow =>
    Object.fromEntries(Object.entries(baselineRowsRef.current).map(([rowId, baseline]) => [rowId, acknowledgedWarningsRef.current.get(baseline) ?? []]));

  /**
   * Record accepted warnings, so neither a later edit of the same field nor Save asks again. They are
   * recorded against the baselines the question was asked about, so a row reopened since is asked afresh.
   * @param warnings the row-and-warning pairs that were accepted
   * @param baselines rowId -> the row baselines when the question was raised
   */
  const acknowledgeWarnings = (warnings: AcknowledgedWarning[], baselines: Record<string, TableRow>) => {
    warnings.forEach(({ rowId, warning }) => {
      const baseline = baselines[rowId];
      if (!baseline) return;
      const accepted = acknowledgedWarningsRef.current.get(baseline) ?? [];
      if (!accepted.includes(warning)) acknowledgedWarningsRef.current.set(baseline, [...accepted, warning]);
    });
  };

  /**
   * Queue dialogs behind any already open; they are asked one at a time.
   * @param changes the dialogs to ask, one per change
   * @param onAccepted runs once every dialog of this group is accepted
   * @param onCancelled runs when any dialog of this group is cancelled
   */
  const enqueueConfirmation = (changes: DestructiveChange[], onAccepted: () => void, onCancelled: () => void) => {
    confirmationSeqRef.current += 1;
    const group: ConfirmationGroup = { id: confirmationSeqRef.current, changes, onAccepted, onCancelled };
    setConfirmationQueue((queue) => [...queue, group]);
  };

  /** Accept the dialog on screen; the group's follow-up runs once its last dialog is accepted. */
  const acceptActiveConfirmation = () => {
    if (!activeConfirmation) return;
    if (activeConfirmation.changes.length > 1) {
      setConfirmationQueue(([head, ...rest]) => (head ? [{ ...head, changes: head.changes.slice(1) }, ...rest] : rest));
      return;
    }
    setConfirmationQueue((queue) => queue.slice(1));
    activeConfirmation.onAccepted();
  };

  /** Cancel the dialog on screen, dropping the rest of its group. */
  const cancelActiveConfirmation = () => {
    if (!activeConfirmation) return;
    setConfirmationQueue((queue) => queue.slice(1));
    activeConfirmation.onCancelled();
  };

  /**
   * Put a cancelled edit back, on every row it touched, including the fields it reset.
   * @param before rowId -> row before the edit
   * @param after rowId -> row right after the edit
   */
  const revertDestructiveEdit = (before: Record<string, TableRow>, after: Record<string, TableRow>) => {
    Object.keys(after).forEach((rowId) => {
      const undo = (current: TableRow) => revertEdit(current, before[rowId] as TableRow, after[rowId] as TableRow);
      if (isDraftId(rowId)) updateDraft(rowId, undo);
      else setEditRows((prev) => (prev[rowId] ? { ...prev, [rowId]: undo(prev[rowId] as TableRow) } : prev));
    });
  };

  /**
   * Ask about a destructive edit the moment it is made, as the product page does: Cancel puts the
   * old values back, Confirm keeps the edit and the dependent data is removed when the rows are saved.
   * A Product Identifier axis is checked on the rows as they would be saved, so a Brand or scope
   * change that clears sizes or Variable 2 is asked about here too, not left for Save.
   * A row closed or reopened while the server check ran is not asked about.
   * @param before rowId -> row before the edit
   * @param after rowId -> row right after the edit
   * @returns true when a dialog was raised
   */
  const reviewDestructiveEdit = async (before: Record<string, TableRow>, after: Record<string, TableRow>): Promise<boolean> => {
    const plan = await planDestructiveEdit(before, after);
    if (!plan) return false;
    if (!plan.toAsk.length) {
      acknowledgeWarnings(plan.warnings, plan.baselines);
      return false;
    }
    enqueueConfirmation(plan.toAsk, () => acknowledgeWarnings(plan.warnings, plan.baselines), () => revertDestructiveEdit(before, after));
    return true;
  };

  /**
   * Review a typed or picked cell edit. Every field is reviewed, not only the destructive ones,
   * because a field such as Supplier(s) cascades into one (its factories); an edit that changes
   * nothing reviewed returns before any server check.
   * @param rowId the edited row
   * @param field the field written
   * @param value the value written
   * @param normalise reduces option objects to the ids the rows hold
   */
  const reviewCellEdit = (rowId: string, field: string, value: unknown, normalise: Parameters<typeof applyFieldChange>[3]) => {
    const before = isDraftId(rowId) ? draftRowsRef.current.find((draft) => draft._id === rowId) : editRowsRef.current[rowId];
    const after = applyFieldChange(before ?? null, field, value, normalise);
    if (before && after) trackEditReview(reviewDestructiveEdit({ [rowId]: before }, { [rowId]: after }));
  };

  /**
   * Review a fill or paste as one edit across its rows, on exactly the terms a cell edit is
   * reviewed: only what this gesture changed is asked about. Comparing against the baseline instead
   * re-asked, on an unrelated fill such as Month, about a change still waiting on its own check.
   * @param batch the cells the gesture wrote
   * @param normalise reduces option objects to the ids the rows hold
   */
  const reviewFilledCells = (batch: BombiixFillBatchItem<TableRow>[], normalise: Parameters<typeof applyFieldChange>[3]) => {
    const before: Record<string, TableRow> = {};
    const after: Record<string, TableRow> = {};
    batch.forEach(({ rowId, field, newValue }) => {
      const key = String(rowId);
      const current = after[key] ?? editRowsRef.current[key];
      if (!current) return;
      before[key] = before[key] ?? current;
      const updated = applyFieldChange(current, field, newValue, normalise);
      if (updated) after[key] = updated;
    });
    trackEditReview(reviewDestructiveEdit(before, after));
  };

  /**
   * Make a running edit check visible to Save, so a Save pressed before the check answers waits for
   * it instead of asking the same question a second time.
   * @param review the running check, resolving true when it raised a dialog
   */
  const trackEditReview = (review: Promise<boolean>) => {
    const tracked = review.catch(() => false);
    editReviewsRef.current.add(tracked);
    tracked.then(() => editReviewsRef.current.delete(tracked)).catch(() => undefined);
  };

  /**
   * Run the cleanup for the destructive changes being saved, then send the rows. Anything not yet
   * confirmed — a change the edit-time check could not settle — is asked about first. One save runs
   * at a time, so a double click cannot ask twice or clean up twice; and a Save pressed while an edit
   * check is still running stops once that check raises its dialog, leaving the user to answer it
   * and save again.
   * @param destructive every destructive change in the rows being saved
   * @param commit sends the rows
   * @param narrow keeps only the products that really lose data; a duplicate draft is already narrowed
   */
  const settleDestructiveThenCommit = async (
    destructive: DestructiveChange[],
    commit: () => Promise<void>,
    narrow: typeof narrowDestructiveByServerState = narrowDestructiveByServerState,
  ) => {
    const finish = async () => {
      await destructive.reduce((previous, change) => previous.then(() => runDestructiveEffects(change)), Promise.resolve());
      await commit();
    };
    await exclusiveSettle(async () => {
      const editChecks = await Promise.all([...editReviewsRef.current]);
      if (editChecks.some(Boolean)) return;
      const unconfirmed = await narrow(unacknowledgedChanges(destructive, acknowledgedByRow()));
      if (!unconfirmed.length) {
        await finish();
        return;
      }
      const baselines = { ...baselineRowsRef.current };
      const onAccepted = () => {
        acknowledgeWarnings(warningsOf(unconfirmed), baselines);
        exclusiveSettle(finish).catch(() => undefined);
      };
      enqueueConfirmation(mergeByWarning(unconfirmed), onAccepted, () => undefined);
    });
  };

  /**
   * Work out the dialogs one edit would raise, asking the server where the grid cannot decide. A
   * Product Identifier axis is checked on the rows as they would be saved, so a Brand or scope change
   * that clears sizes or Variable 2 counts as surely as a hand edit to the axis.
   * @param before rowId -> row before the edit
   * @param after rowId -> row right after the edit
   * @returns the dialogs to raise and the warnings they settle, or null when the edit raises nothing
   *   or a row was closed or reopened while the server check ran
   */
  const planDestructiveEdit = async (
    before: Record<string, TableRow>,
    after: Record<string, TableRow>,
  ): Promise<{ toAsk: DestructiveChange[]; warnings: AcknowledgedWarning[]; baselines: Record<string, TableRow> } | null> => {
    const baselines = { ...baselineRowsRef.current };
    const isDraft = Object.keys(after).some(isDraftId);
    const candidates = isDraft ? collectDraftDestructiveChanges(after, baselines, gradingLibraryRef.current) : collectDestructiveChanges(after, baselines);
    const asSaved = (editedRows: Record<string, TableRow>) =>
      Object.fromEntries(
        Object.entries(
          applyDestructiveCascades(editedRows, baselines, customAttributesRef.current, multiCustomAttributesRef.current, gradingLibraryRef.current),
        ).map(([rowId, row]) => [rowId, reconcileAdditionalVariables(row, baselines[rowId])]),
      );
    const savedAfter = isDraft ? after : asSaved(after);
    const introduced = [
      ...changesIntroducedBy(candidates, before, after, acknowledgedByRow()),
      ...(isDraft ? [] : identifierChangesIntroducedBy(asSaved(before), savedAfter, baselines, acknowledgedByRow())),
    ];
    if (!introduced.length) return null;
    const narrowed = isDraft ? introduced : await narrowDestructiveByServerState(introduced, savedAfter);
    if (Object.keys(after).some((rowId) => baselineRowsRef.current[rowId] !== baselines[rowId])) return null;
    return { ...planEditConfirmation(narrowed), baselines };
  };

  multiPickPreviewRef.current = async (rowId, field, value) => {
    const before = isDraftId(rowId) ? draftRowsRef.current.find((draft) => draft._id === rowId) : editRowsRef.current[rowId];
    const after = applyFieldChange(before ?? null, field, value, optionIdsOf);
    if (!before || !after) return false;
    const plan = await planDestructiveEdit({ [rowId]: before }, { [rowId]: after });
    return Boolean(plan?.toAsk.length);
  };

  /** Identifier axis usage per opened row, keyed by the row's baseline so a reopened row reads it afresh. */
  const axisUsageByBaselineRef = useRef(new WeakMap<TableRow, Promise<IdentifierAxisUsage | undefined>>());

  /**
   * Whether a product holds DAM assets, asked once per check.
   * @param productId the product to check
   * @returns whether the product has DAM assets
   */
  const checkDamOnce = (productId: string): Promise<boolean> => {
    const cached = usageCheckRef.current.get(productId);
    if (cached) return cached;
    const pending = Promise.resolve(dispatch(checkDamExist(productId))).then(Boolean);
    usageCheckRef.current.set(productId, pending);
    pending.catch(() => usageCheckRef.current.delete(productId));
    return pending;
  };

  /**
   * Whether saving this row would delete Product Identifier rows, asked of the server exactly as
   * CreateProduct's warning asks it. The axis usage is read once per opened row (cached against the
   * row's baseline, which every open replaces), so repeated picks in a multi-select do not each
   * call the server. A failed read falls back to the saved product's own selections, which errs on
   * the side of warning rather than letting the deletion through unasked.
   * @param productId the product being saved
   * @param row the row as it will be saved
   * @returns whether identifier rows would be lost
   */
  const identifierRowsAtRisk = async (productId: string, row: TableRow | undefined): Promise<boolean> => {
    const baseline = baselineRowsRef.current[productId];
    const cached = baseline ? axisUsageByBaselineRef.current.get(baseline) : undefined;
    const pending = cached ?? fetchProductIdentifierAxisUsage(productId).catch(() => undefined);
    if (baseline && !cached) axisUsageByBaselineRef.current.set(baseline, pending);
    const usage = await pending;
    return wouldDeleteIdentifierRows(usage ?? usageFromSavedProduct(baseline), selectionsFromProduct(row), selectionsFromProduct(baseline));
  };

  /**
   * Drop a Variable 2 selection that no longer belongs to the row's brand / product type / category scope.
   * Only runs when one of those four fields changed; while the variable list has not loaded the selection is left alone.
   */
  const reconcileAdditionalVariables = (row: TableRow, baseline: TableRow | undefined): TableRow => {
    const variables = cascadeMastersRef.current.additionalVariables;
    if (!row.additionalVariables || !variables || additionalVariableScopeKey(row) === additionalVariableScopeKey(baseline)) return row;
    const matched = matchedAdditionalVariable(row, variables);
    const selection = row.additionalVariables as { variableId?: unknown };
    return matched && String(matched._id) === String(selection.variableId) ? row : { ...row, additionalVariables: null };
  };

  /**
   * Show a failed single save: on the cell the server key names, when it names one, and always as a
   * toast. A failure that is not about one field (no access, product missing) marks no cell.
   * @param rowId the row whose save failed
   * @param failure the failure the server returned for that row
   */
  const showSingleSaveFailure = (rowId: string, failure: BulkUpdateFailure) => {
    const field = fieldForSaveError(failure.errMsg);
    if (field) setRowErrors({ [rowId]: { [field]: failure.errMsg } });
    toasterMsg(describeSaveFailure(failure), 'danger');
  };

  /**
   * The message for a failed save. A sold-country rejection names the countries it refused, the way
   * the Edit Product endpoint's 400 lists them; any other failure is its translated key.
   * @param failure the failure the server returned for one product
   * @returns the text to show the user
   */
  const describeSaveFailure = (failure: BulkUpdateFailure): string => {
    const slugs = failure.removedSlugs ?? failure.invalidSlugs ?? [];
    if (!slugs.length) return translateServerKey(failure.errMsg);
    const countries = slugs.map((slug) => nameMapsRef.current.soldCountries?.[slug] || slug).join(', ');
    return t(`product-information:${failure.errMsg}_NAMED`, { countries }) as string;
  };

  /**
   * Keep the factories in step with the suppliers, as the product page does: removing a supplier
   * (including the reset a Product Type change makes) drops the factories no selected supplier
   * offers — except those the product already consumes. Only adding suppliers drops nothing, so it
   * never raises the factory-removal warning. Either way a supplier set offering one factory has it
   * picked. Any
   * change to the factories then refreshes `factoryCountry`, which the product page saves with them.
   * @param prev the row before the edit
   * @param next the row after the edit and its other cascades
   * @returns the row with factories and factory countries brought in line
   */
  const applySupplierFactoryCascade = (prev: TableRow, next: TableRow): TableRow => {
    const { supplierFactories } = cascadeMastersRef.current;
    const idsOf = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);
    let updated = next;
    if (idsOf(prev.suppliers).join() !== idsOf(next.suppliers).join()) {
      const removedSupplier = idsOf(prev.suppliers).some((supplierId) => !idsOf(next.suppliers).includes(supplierId));
      const alwaysKept = new Set(
        removedSupplier ? idsOf((next.__product as Record<string, unknown> | undefined)?.orderFactoryId) : idsOf(next.supplierFactories),
      );
      updated = {
        ...updated,
        supplierFactories: reconcileSupplierFactories(supplierFactories, idsOf(next.suppliers), idsOf(next.supplierFactories), alwaysKept),
      };
    }
    if (idsOf(prev.supplierFactories).join() === idsOf(updated.supplierFactories).join()) return updated;
    return { ...updated, factoryCountry: factoryCountriesFor(supplierFactories, idsOf(updated.supplierFactories)) };
  };

  /**
   * The thumbnail the product list already returned for a product, whether a top-level row or a
   * loaded cluster child, so a row opened for edit keeps it rather than showing the full image.
   * @param productId the product to look up
   * @returns its list thumbnail, or an empty string when the list holds none
   */
  const listedThumbnailOf = (productId: string): string => {
    const isProduct = (item: ProductItem) => String(item._id) === productId;
    const listed = productList.find(isProduct) ?? Object.values(reorderFullById).flat().find(isProduct);
    return listed?.thumbNailImg ?? '';
  };

  /**
   * Mark the rows missing a required field, take the user to the first one and name the fields.
   * Shared by the bulk edit and the multi-add saves.
   * @param errorsByRow validation errors keyed by row id, then field
   */
  const reportMissingRequired = (errorsByRow: Record<string, Record<string, string>>) => {
    setRowErrors(errorsByRow);
    const missing = revealFirstInvalidBulkCell(errorsByRow);
    const rowCount = Object.keys(errorsByRow).length;
    toasterMsg(
      missing.length
        ? String(t('product-information:REQUIRED_FIELDS_DETAIL', { fields: missing.join(', '), count: rowCount }))
        : String(t('product-information:REQUIRED_FIELDS_MESSAGE')),
      'warning',
    );
  };

  const warnDraftCap = () => toasterMsg(String(t('product-information:BULK_ADD_MAX_ROWS', { max: MAX_MULTI_ADD_PRODUCTS })), 'warning');

  /** The floating bar's "+": append a blank draft (default status pre-selected). */
  const handleAppendDraft = () => {
    if (!appendDraft({ ...emptyDraftRow(), status: defaultStatusId })) warnDraftCap();
  };

  /** A draft row's "+": copy it into a new draft directly below, as Placeholder Management does. */
  const handleAddDraftBelow = (row: TableRow) => {
    if (!addDraftBelow(String(row._id))) warnDraftCap();
  };

  /** A draft row's Delete: drop it locally — nothing was saved, so there is nothing to confirm. */
  const handleRemoveDraft = (row: TableRow) => {
    const rowId = String(row._id);
    removeDraft(rowId);
    setRowErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== rowId)));
    if (descSearchRowId === rowId) setDescSearchRowId('');
  };

  /**
   * Save drafts in one request (BM-9594): one row from its own Save, or every draft from the floating
   * bar's Save. The drafts get the same checks a single add gets — required fields and custom-attribute
   * character limits — before anything is sent. The row count needs no check here: useDraftRows never
   * opens more than the cap.
   * @param drafts the drafts to create
   */
  const handleSaveDrafts = async (drafts: TableRow[]) => {
    if (!drafts.length) return;
    const draftsById: Record<string, TableRow> = Object.fromEntries(drafts.map((row) => [String(row._id), row]));
    const errorsByRow = validateInlineRows(draftsById);
    if (Object.keys(errorsByRow).length) {
      reportMissingRequired(errorsByRow);
      return;
    }
    const overLimit = drafts
      .map((row) => [String(row._id), blockingCharLimits(row)] as const)
      .filter(([, broken]) => Object.keys(broken).length);
    const [firstOverLimit] = overLimit;
    if (firstOverLimit) {
      revealFirstInvalidBulkCell(
        Object.fromEntries(overLimit.map(([rowId, broken]) => [rowId, Object.fromEntries(Object.keys(broken).map((field) => [field, 'over-limit']))])),
      );
      toasterMsg(t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit: Object.values(firstOverLimit[1])[0] }), 'danger');
      return;
    }
    await runExclusiveSave(() => commitBulkCreate(draftsById));
  };

  /**
   * Send the drafts and apply the per-row outcome: created drafts close, rejected ones stay open with
   * the reason on the row, drafts outside this save are left as they are, and the list is refreshed
   * once if anything was created. Every rejection is named in one toast, so a large failed batch does
   * not queue a toast per row.
   * @param draftsById the drafts to create, keyed by draft id
   */
  const commitBulkCreate = async (draftsById: Record<string, TableRow>) => {
    const payload = buildBulkCreatePayload(Object.values(draftsById), customAttributes);
    const result = (await dispatch(bulkCreateProducts(payload, t))) as BulkCreateResult | null;
    if (!result) return;
    const { createdIds, errorsByRow } = partitionBulkCreateResult(result);
    dropDrafts(createdIds);
    setRowErrors((prev) => ({ ...Object.fromEntries(Object.entries(prev).filter(([rowId]) => !draftsById[rowId])), ...errorsByRow }));
    if (createdIds.has(descSearchRowId)) setDescSearchRowId('');
    if (result.successCount > 0) refreshList();
    toasterMsg(
      String(t('product-information:BULK_CREATE_SUMMARY', { saved: result.successCount, failed: result.failureCount })),
      result.failureCount > 0 ? 'warning' : 'success',
    );
    const failureLines = result.failed.map((entry) => {
      const label = (draftsById[entry.clientRowId]?.number as string) || '';
      return label ? `${label}: ${translateServerKey(entry.errMsg)}` : translateServerKey(entry.errMsg);
    });
    if (failureLines.length) toasterMsg(failureLines.join('; '), 'danger');
  };


  /**
   * Settle the dialog on screen without asking when its warning was accepted, for every product it
   * names, while it waited behind another dialog — two quick edits otherwise asked the same question twice.
   */
  useEffect(() => {
    if (activeDestructive && isChangeAcknowledged(activeDestructive, acknowledgedByRow())) acceptActiveConfirmation();
  });

  return (
    <>
      <div className="page-title-filter sticky product-info-title-bar">
        <ProductInfoSubHeader
          linkOpen={false}
          tableView
          onAddProduct={handleStartAdd}
          inlineEditMode={floatyMode}
          inlineEditCount={floatyMode === 'draft' ? draftRows.length : dirtyRowCount}
          inlineEditBusy={isSavingAll || isHydrating || awaitingRowOptions}
          onBulkEdit={handleStartBulkEdit}
          onBulkSave={floatyMode === 'draft' ? () => handleSaveDrafts(draftRows) : handleSaveAllInline}
          onBulkCancel={handleCancelInline}
          selectedProductCount={selectedProductIds.length}
          onAppendDraft={handleAppendDraft}
          canAppendDraft={draftRows.length < MAX_MULTI_ADD_PRODUCTS}
        />
      </div>
      <Container fluid className="ptv-page">
        {/* Select-all control above the grid (right-aligned): checkbox + "Select All" label,
            wired to the SAME state as the in-grid header checkbox so both stay in sync. Shown
            whenever the current (non-bin) list has products. */}
        {activeList !== 'bin' && productList.length > 0 && (
          <div className="ptv-select-all-bar">
            {/* Above the cap the Edit action disappears; say why rather than leave the user
                hunting for it. Select-all itself stays uncapped — it also drives Archive/Status. */}
            {overSelectionCap && (
              <span className="f-12 text-grey mr-3">{String(t('product-information:BULK_EDIT_SELECTION_CAP', { max: MAX_MULTI_EDIT_PRODUCTS }))}</span>
            )}
            <NewCheckbox
              size="sm"
              className="ptv-select-all-checkbox"
              label={String(t('FILTER.SELECT_ALL'))}
              checked={isSelectAll}
              disabled={isBulkEditing}
              onChange={handleSelectAll}
            />
          </div>
        )}
        <div className="ptv-aggrid-wrapper">
          {/* Both ends of a bulk edit block the grid: opening it reads every selected product's
              detail before the rows can bind, and saving must not accept a cell change that would
              race the request already carrying its old value. The rows stay readable underneath
              rather than being swapped out, so the user keeps sight of what they picked. */}
          {(isSavingAll || isHydrating || awaitingRowOptions || (pageLoading && productList.length > 0)) && (
            <div className="ptv-saving-overlay" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          )}
          {scrollLoader && !pageLoading && (
            <div className="ptv-scroll-loader" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          )}
          {pageLoading && !productList.length ? (
            <Loader />
          ) : (
            <BombiixAGGrid<TableRow>
              rowData={rows}
              refreshChangedRowsOnly
              columnDefs={columnDefs}
              getRowId={getRowId}
              sortable={false}
              height="100%"
              fullWidthHorizontalScroll
              treeData
              getDataPath={getDataPath}
              groupDefaultExpanded={0}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loadingMore={scrollLoader}
              onRowChange={handleRowChange}
              enableFillHandle
              onFillCells={handleFillCells}
              getRowClass={getBulkRowClass}
              renderRowActionExtras={renderRowActionExtras}
              rowActionsWidth={150}
              onDeleteRow={() => undefined}
              canDeleteRow={() => false}
              hideCellErrorMessage
              gridOptions={gridOptions}
            />
          )}
        </div>
        {(UserDetails?.userInfo?.role === 'User' || UserDetails?.userInfo?.role === 'Admin') && (
          <Chat moduleKey="ProductInfoList" isDetailsPage={false} currentUser={UserDetails?.userInfo} activeTab={null} />
        )}
      </Container>

      {/* "+" add-option modals for the inline row's Colourway(s) and multi-custom cells
          (mirror the ProductModal + buttons). */}

      {activeDestructive?.field === 'supplierFactories' ? (
        <ConfirmationPopup
          key={`${activeConfirmation?.id}-${activeDestructive.field}`}
          onOpen
          onConfirm={acceptActiveConfirmation}
          toggle={cancelActiveConfirmation}
          title={t(activeDestructiveCopy?.title ?? 'EDIT_FIELD_MODAL.REMOVE_FACTORY')}
          message={t(activeDestructiveCopy?.message ?? 'EDIT_FIELD_MODAL.FIRST_MESSAGE')}
          secondMessage={t(activeDestructiveCopy?.second ?? 'EDIT_FIELD_MODAL.SECOND_MESSAGE')}
        />
      ) : activeDestructive ? (
        <ConfirmButtonPopup
          key={`${activeConfirmation?.id}-${activeDestructive.field}`}
          onOpen
          requireTypedConfirm
          title={t(activeDestructiveCopy?.title ?? 'product-information:DESTRUCTIVE_CHANGE_TITLE')}
          message={t(activeDestructiveCopy?.message ?? 'product-information:DESTRUCTIVE_CHANGE_TITLE', activeDestructiveCopy?.params)}
          secondMessage={
            activeDestructive.field === 'supplierType' ? (
              <ProductTypeChangeWarning />
            ) : (
              t(activeDestructiveCopy?.second ?? 'product-information:DESTRUCTIVE_CHANGE_WARNING')
            )
          }
          onConfirm={acceptActiveConfirmation}
          toggle={cancelActiveConfirmation}
          className={activeDestructive.field === 'supplierType' ? 'product-type-change-modal' : undefined}
        />
      ) : null}

      {addModal?.kind === 'colourway' && <CommonColorWayAdd onOpen toogle={handleCloseColourwayAdd} imageUpload={() => undefined} hideDocument />}
      {addModal?.kind === 'multiCustom' && (
        <AddMultiDropOpt
          onOpen
          onSubmit={(data: unknown) => handleSaveMultiCustom(data)}
          bomTypeId={addModal.attributeId}
          multiDynamicAttribute={multiDynamicAttribute}
          loggedInUserId={userId}
          loading={false}
          toggle={() => setAddModal(null)}
          label={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_TYPE')}
        />
      )}
      {addModal?.kind === 'singleCustom' && (
        <AddCustomDropOpt
          onOpen
          onSubmit={(data: unknown) => handleSaveSingleCustom(data)}
          bomTypeId={addModal.attributeId}
          multiDynamicAttribute={customAttributes.filter((attr) => attr.type === 'dropdown' || attr.type === 'radio-button')}
          loggedInUserId={userId}
          loading={false}
          toggle={() => setAddModal(null)}
          label={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_TYPE')}
        />
      )}

      {state?.isCompletionModalOpen && (
        <CompletionModal
          item={state?.currentProduct}
          show={state?.isCompletionModalOpen}
          completionData={state?.completionData}
          handleClose={handleCloseCompletion}
        />
      )}
      {state?.isProductGroupModalOpen && (
        <ProductGroupModal
          item={state?.currentProduct}
          show={state?.isProductGroupModalOpen}
          handleClose={handleCloseGroup}
          handleOpenProductGroup={handleOpenProductGroup}
          groupId={state?.selectedGroupId}
        />
      )}
      {/* "Create new product group" modal opened from the floating Add-to-Group control's "+".
          Mirrors the card page so the table view's group button is fully functional. */}
      {state?.isAddProductGroup && (
        <AddProductGroupModal
          title={String(t('FILTER.ADD_PRODUCT_GROUPS'))}
          label={String(t('USER_GROUP_VIEW.NAME'))}
          show={state?.isAddProductGroup}
          handleClose={handleCloseAddProductGroup}
          handleProductGroupInput={handleProductGroupInput}
          newProductGroup={state?.newProductGroup}
          hasError={state?.groupError}
          handleAddProductGroup={handleAddProductGroup}
        />
      )}
      {state?.isArchivedModal && (
        <BombiixModal
          show={state?.isArchivedModal}
          title={activeList === 'open' ? String(t('USER_GROUP_VIEW.ARCHIVE')) : String(t('USER_GROUP_VIEW.UNARCHIVE'))}
          onClose={closeModals}
          onSave={handleArchivedConfirmed}
          saveLabel={activeList === 'open' ? String(t('USER_GROUP_VIEW.ARCHIVE')) : String(t('USER_GROUP_VIEW.UNARCHIVE'))}
          saveLoading={state?.loader}
          size="md"
        >
          <p className="mb-16 text-center">
            {`${t('USER_GROUP_VIEW.SURE_MESSAGE')} ${activeList === 'open' ? t('USER_GROUP_VIEW.ARCHIVE_LOWER') : t('USER_GROUP_VIEW.UNARCHIVE_LOWER')} ${t(
              'USER_GROUP_VIEW.SELECTED_PRODUCTS',
            )}`}
          </p>
          <NewTextArea
            rows={3}
            resize="vertical"
            maxLength={200}
            value={state?.archivedComment}
            placeholder={String(t('CONFIRMATION_POPUP.ENTER_COMMENT'))}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setState((prev) => ({ ...prev, archivedComment: event.target.value }))}
          />
          <div className="character-counter">{state?.archivedComment?.length || 0}/200</div>
        </BombiixModal>
      )}
      {state?.isTrashModal && (
        <BombiixModal
          show={state?.isTrashModal}
          title={String(t('FILTER.DELETE_PRODUCT'))}
          onClose={closeModals}
          onSave={handleTrashConfirmed}
          saveLoading={state?.loader}
          size="md"
        >
          <p className="mb-16 text-center">{String(t('FILTER.SELECTED_BIN_MESSAGE'))}</p>
        </BombiixModal>
      )}
      {lockedRemovalNotice && (
        <BombiixModal
          show={Boolean(lockedRemovalNotice)}
          title={String(tProductInfo(lockedRemovalNotice.field === 'suppliers' ? 'SUPPLIER_LOCK.TITLE' : 'FACTORY_LOCK.TITLE'))}
          onClose={() => setLockedRemovalNotice(null)}
          hideSave
          size="md"
        >
          <p className="mb-0 text-center">
            {String(
              lockedRemovalNotice.field === 'suppliers'
                ? tProductInfo('SUPPLIER_LOCK.MESSAGE', { supplierNames: lockedRemovalNotice.names })
                : tProductInfo('FACTORY_LOCK.MESSAGE', { factoryNames: lockedRemovalNotice.names }),
            )}
          </p>
        </BombiixModal>
      )}
      {state?.isDeleteOpen && (
        <ConfirmationPopup
          onOpen={state?.isDeleteOpen}
          onConfirm={handleDeleteConfirmed}
          title={String(t('FILTER.DELETE_PRODUCT'))}
          message={String(t('FILTER.SELECTED_BIN_PERMANETLY_MESSAGE'))}
          toggle={closeModals}
          isCommentOn={false}
        />
      )}
      {state?.isUnlinkOpen && (
        <BombiixModal
          show={state?.isUnlinkOpen}
          title={String(t('DEVLOPMENT_ORDER.PRODUCT_LIST_CONTAINER.PRODUCT_CARD_LIST.UNLIKE_PRODUCT'))}
          onClose={closeModals}
          onSave={handleUnlinkConfirmed}
          saveLabel={String(t('FILTER.UNLIKE'))}
          size="md"
        >
          <p className="mb-16 text-center">{String(t('DEVLOPMENT_ORDER.PRODUCT_LIST_CONTAINER.PRODUCT_CARD_LIST.PRODUCT_UNLIKE_MESSAGE'))}</p>
        </BombiixModal>
      )}
      {/* Discarding is the destructive choice, so it carries the red variant; the header Close
          is the safe way out and leaves the edits open. */}
      {pendingExit !== null && (
        <BombiixModal
          show
          title={t('product-information:UNSAVED_EDITS_TITLE') as string}
          onClose={() => setPendingExit(null)}
          onSave={handleDiscardAndExit}
          saveLabel={t('product-information:UNSAVED_EDITS_DISCARD') as string}
          saveVariant="red"
          size="md"
        >
          <p className="mb-16 text-center">{t('product-information:UNSAVED_EDITS_MESSAGE', { count: dirtyRowCount + draftRows.length }) as string}</p>
        </BombiixModal>
      )}
    </>
  );
}

export default ProductTableView;
